<template>
    <div class="cgraph-wrap" :class="{ 'cgraph-fullscreen': isFullscreen }">
        <!-- Toolbar -->
        <div class="cgraph-toolbar" v-if="hasGraph">
            <div class="cgraph-tb-left">
                <span class="cgraph-stats">
                    <i class="fa fa-circle-nodes me-1 opacity-50"></i>
                    {{ graphStats.nodes }} nodes
                    <span class="cgraph-stats-sep">·</span>
                    {{ graphStats.edges }} edges
                    <template v-if="showBridgeStats && graphStats.sharedEntities">
                        <span class="cgraph-stats-sep">·</span>
                        <span class="cgraph-stats-shared">{{ graphStats.sharedEntities }} shared</span>
                    </template>
                    <template v-if="clusterEdgeCount > 0">
                        <span class="cgraph-stats-sep">·</span>
                        <span
                            class="cgraph-stats-cluster-edges"
                            :class="{ 'is-idle': !graphStats.drawnClusterEdges }"
                            :title="graphStats.drawnClusterEdges
                                ? 'Cluster edges currently shown, of the total this recommendation has. Each is bidirectional.'
                                : 'This recommendation has cluster edges joining the two clusters. Click a node to show the ones that belong to it.'"
                        >
                            <template v-if="graphStats.drawnClusterEdges">
                                {{ graphStats.drawnClusterEdges }} of {{ clusterEdgeCount }} cluster edges
                            </template>
                            <template v-else>
                                {{ clusterEdgeCount }} cluster {{ clusterEdgeCount === 1 ? 'edge' : 'edges' }} — click a node
                            </template>
                        </span>
                    </template>
                    <template v-if="graphStats.collapsedEntities > 0">
                        <span class="cgraph-stats-sep">·</span>
                        {{ graphStats.collapsedEntities }} collapsed
                    </template>
                </span>
            </div>
            <div class="cgraph-tb-right">
                <button class="cgraph-tb-btn" @click="zoomIn" title="Zoom in"><i class="fa fa-magnifying-glass-plus"></i></button>
                <button class="cgraph-tb-btn" @click="zoomOut" title="Zoom out"><i class="fa fa-magnifying-glass-minus"></i></button>
                <button class="cgraph-tb-btn" @click="fitToScreen" title="Fit to screen"><i class="fa fa-down-left-and-up-right-to-center"></i></button>
                <button
                    class="cgraph-tb-btn"
                    @click="resetView"
                    title="Reset — returns dragged nodes to their place, closes any open node and clears filters"
                ><i class="fa fa-rotate-left"></i></button>
                <div class="cgraph-tb-sep"></div>
                <button class="cgraph-tb-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'">
                    <i :class="isFullscreen ? 'fa fa-compress' : 'fa fa-expand'"></i>
                </button>
            </div>
        </div>

        <!-- Legend -->
        <div
            class="cgraph-legend"
            :class="{ 'is-wrapped': legendExpanded }"
            v-if="hasGraph && legendItems.length"
        >
            <template v-for="item in visibleLegendItems" :key="item.type">
                <button
                    v-if="!item.legendOverflow"
                    type="button"
                    class="cgraph-legend-chip"
                    :class="{ 'is-off': hiddenTypes.has(item.type) }"
                    :title="item.full || item.label"
                    @click="toggleType(item.type)"
                >
                    <span class="cgraph-legend-dot" :style="{ background: hiddenTypes.has(item.type) ? '#bbb' : item.color }"></span>
                    <span class="cgraph-legend-label">{{ item.label }}</span>
                    <span class="cgraph-legend-cnt">{{ item.count }}</span>
                </button>
                <button v-else type="button" class="cgraph-legend-chip cgraph-legend-more" @click="legendExpanded = !legendExpanded">
                    <span class="cgraph-legend-label">{{ item.label }}</span>
                    <i :class="legendExpanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="font-size:8px;margin-left:3px;opacity:0.7"></i>
                </button>
            </template>
        </div>

        <!-- SVG canvas -->
        <!-- Canvas area: inspector floats over SVG, never shifts layout -->
        <div class="cgraph-canvas-area">
            <transition name="cgraph-insp">
                <div
                    v-if="hasGraph && graphInspector.visible"
                    class="cgraph-inspector"
                    role="status"
                    aria-live="polite"
                >
                    <span class="cgraph-insp-chip" :style="{ '--ic': graphInspector.color }">
                        <span class="cgraph-insp-dot"></span>
                        <span class="cgraph-insp-label">{{ graphInspector.fullLabel }}</span>
                    </span>
                    <span class="cgraph-insp-meta">
                        <template v-for="(ln, i) in graphInspector.lines" :key="i">
                            <span v-if="i > 0" class="cgraph-inspector-sep">·</span>{{ ln }}
                        </template>
                    </span>
                </div>
            </transition>
            <div v-if="hasGraph && laneHeaders.length === 2" class="cgraph-lane-hdrs">
                <span
                    v-for="lh in laneHeaders"
                    :key="'lh-' + lh.side"
                    class="cgraph-lane-hdr"
                    :class="'cgraph-lane-hdr-' + lh.side"
                    :style="{ '--lane-color': lh.color }"
                    :title="lh.full"
                >
                    <span class="cgraph-lane-dot"></span>
                    <span class="cgraph-lane-name">{{ lh.label }}</span>
                    <span v-if="lh.full && lh.full !== lh.label" class="cgraph-lane-id">{{ lh.full }}</span>
                </span>
            </div>
            <div ref="graphContainer" class="cgraph-canvas"></div>
        </div>

        <!-- Detail panel with slide-in transition -->
        <transition name="cgraph-slide">
            <div
                v-if="detailNode"
                class="cgraph-panel"
                :class="{
                    'cgraph-panel--wide':
                        !panelCollapsed && (
                            detailNode.type === 'entity_group' ||
                            detailNode.type === 'entity_bundle' ||
                            (detailNode.clusterEdgeDetails || []).length > 0
                        ),
                    'cgraph-panel--collapsed': panelCollapsed,
                    'cgraph-panel--placed': !!panelPos,
                }"
                :style="panelStyle()"
            >
                <!-- Drag by the header. The panel overlays the canvas, so being movable off
                     whatever it covers is the whole of its escape hatch. -->
                <div
                    class="cgraph-panel-hdr"
                    :style="{ '--node-color': nodeFill(detailNode) }"
                    title="Drag to move"
                    @mousedown="startPanelDrag"
                >
                    <div class="cgraph-panel-hdr-inner">
                        <span class="cgraph-panel-icon-badge">
                            <i :class="panelIconClass(detailNode)"></i>
                        </span>
                        <span class="cgraph-panel-hdr-title">{{ panelTitle(detailNode) }}</span>
                    </div>
                    <div class="cgraph-panel-hdr-actions" @mousedown.stop>
                        <button
                            class="cgraph-panel-btn"
                            title="Move clear of the graph — picks the emptiest corner"
                            aria-label="Move panel clear of the graph"
                            @click="placePanelClear"
                        ><i class="fa fa-arrows-to-dot"></i></button>
                        <button
                            class="cgraph-panel-btn"
                            :title="panelCollapsed ? 'Expand' : 'Collapse to the title bar'"
                            :aria-label="panelCollapsed ? 'Expand panel' : 'Collapse panel'"
                            @click="panelCollapsed = !panelCollapsed"
                        ><i :class="panelCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i></button>
                        <button class="cgraph-panel-btn" @click="detailNode = null" aria-label="Close"><i class="fa fa-xmark"></i></button>
                    </div>
                </div>
                <div v-show="!panelCollapsed" class="cgraph-panel-body">
                    <!-- Entity group. The ids carry the detail: how many cluster edges each one
                         has, and — on demand — which groups on the other side those edges reach. -->
                    <template v-if="detailNode.type === 'entity_group'">
                        <div class="cgraph-panel-meta-row">
                            <span class="cgraph-type-pill">{{ detailNode.neighborType || detailNode.key || 'type~domain' }}</span>
                            <span class="cgraph-cnt-badge">{{ entityGroupIdRows.length }} IDs</span>
                            <span v-if="detailNode.clusterEdgeCount" class="cgraph-flag cgraph-flag--rose">
                                <i class="fa fa-link me-1"></i>{{ detailNode.clusterEdgeCount }}
                                {{ detailNode.clusterEdgeCount === 1 ? 'connection' : 'connections' }}
                            </span>
                            <span v-else-if="detailNode.relationUnconnected" class="cgraph-flag cgraph-flag--muted">
                                No cross-cluster edge
                            </span>
                        </div>
                        <p v-if="detailNode.isRelationSubNode" class="cgraph-panel-note">
                            <template v-if="(detailNode.relationTargets || []).length">
                                Id <strong>{{ detailNode.entityId }}</strong> of
                                <strong>{{ detailNode.neighborType }}</strong> — joined to
                                <span
                                    v-for="(t, ti) in detailNode.relationTargets"
                                    :key="'rt-' + ti"
                                    class="cgraph-type-pill cgraph-type-pill--sm"
                                >{{ t }}</span>
                            </template>
                            <template v-else>
                                Id <strong>{{ detailNode.entityId }}</strong> of
                                <strong>{{ detailNode.neighborType }}</strong> — no cross-cluster edge.
                            </template>
                        </p>

                        <!-- Read-only: what each id of this type~domain reaches in the other
                             cluster. Acting on it happens on the canvas — drag an exploded id to
                             the other hub, or use its merge pill — so nothing here is a control.
                             A panel that both informed and acted meant closing it to see the
                             graph, then reopening it to act. -->
                        <template v-if="!detailNode.isRelationSubNode && entityGroupIdRows.length">
                            <div class="cgraph-section-lbl">
                                IDs and their edges to the other cluster
                            </div>
                            <p class="cgraph-panel-note">
                                <template v-if="isNodeExpanded(detailNode.id)">
                                    Each id is a node on the graph — drag one across to merge it.
                                </template>
                                <template v-else>
                                    Click the group on the graph to explode it into one node per id.
                                </template>
                                <template v-if="explodedOverflowCount">
                                    Showing {{ entityGroupIdRows.length - explodedOverflowCount }}
                                    of {{ entityGroupIdRows.length }} on the graph; all
                                    {{ entityGroupIdRows.length }} are listed here.
                                </template>
                            </p>
                            <div class="cgraph-idinfo">
                                <div
                                    v-for="(row, ii) in visibleEntityGroupIdRows"
                                    :key="'idinfo-' + ii"
                                    class="cgraph-idinfo-row"
                                    :class="{ 'is-linked': row.total > 0 }"
                                >
                                    <span class="cgraph-idinfo-id">{{ row.id }}</span>
                                    <span
                                        class="cgraph-idinfo-cnt"
                                        :class="{ 'is-zero': !row.total }"
                                    >{{ row.total }} {{ row.total === 1 ? 'edge' : 'edges' }}</span>
                                    <span class="cgraph-idinfo-targets">
                                        <template v-if="row.groups.length">
                                            <span
                                                v-for="(grp, gi) in row.groups"
                                                :key="'idinfo-' + ii + '-g-' + gi"
                                                class="cgraph-type-pill cgraph-type-pill--sm"
                                                :title="grp.ids.join(', ')"
                                            >{{ grp.pairLabel || 'unknown' }}<span class="cgraph-idinfo-sub">{{ grp.ids.length }}</span></span>
                                        </template>
                                        <span v-else class="cgraph-rel-none">&mdash;</span>
                                    </span>
                                </div>
                            </div>
                            <button
                                v-if="entityGroupIdRows.length > visibleEntityGroupIdRows.length"
                                type="button"
                                class="cgraph-more-btn"
                                @click="panelIdsShowAll = true"
                            >
                                Show all {{ entityGroupIdRows.length }} IDs
                            </button>
                        </template>
                        <p v-else class="cgraph-empty-note">No IDs parsed for this group.</p>
                        <button
                            v-if="entityGroupIdRows.length > visibleEntityGroupIdRows.length || panelIdsShowAll"
                            type="button"
                            class="btn btn-sm btn-outline-secondary mt-2"
                            @click="panelIdsShowAll = !panelIdsShowAll"
                        >
                            {{ panelIdsShowAll ? 'Show fewer' : `Show all ${entityGroupIdRows.length}` }}
                        </button>
                    </template>

                    <!-- Entity bundle -->
                    <template v-else-if="detailNode.type === 'entity_bundle'">
                        <p class="cgraph-panel-note">Large entity lists are sampled on the graph for performance.</p>
                        <div class="cgraph-stat-grid">
                            <div class="cgraph-stat-cell">
                                <span class="cgraph-stat-lbl">Total</span>
                                <span class="cgraph-stat-val">{{ detailNode.bundleTotal }}</span>
                            </div>
                            <div class="cgraph-stat-cell">
                                <span class="cgraph-stat-lbl">On graph</span>
                                <span class="cgraph-stat-val">{{ detailNode.bundleDisplayed }}</span>
                            </div>
                            <div class="cgraph-stat-cell">
                                <span class="cgraph-stat-lbl">Collapsed</span>
                                <span class="cgraph-stat-val cgraph-stat-val--warn">{{ detailNode.bundleOverflow }}</span>
                            </div>
                        </div>
                        <div v-if="(detailNode.bundleHiddenAllIds || []).length" class="cgraph-btn-row">
                            <button type="button" class="btn btn-sm btn-outline-primary" @click="downloadTextFile('collapsed-ids.txt', (detailNode.bundleHiddenAllIds || []).join('\n'))">
                                <i class="fa fa-download me-1"></i>Download IDs
                            </button>
                            <button v-if="(detailNode.bundleHiddenAllIds || []).length > (detailNode.bundleHiddenPreview || []).length" type="button" class="btn btn-sm btn-outline-secondary" @click="bundlePanelShowAll = !bundlePanelShowAll">
                                {{ bundlePanelShowAll ? 'Show preview' : `Show all ${(detailNode.bundleHiddenAllIds || []).length}` }}
                            </button>
                        </div>
                        <template v-if="bundleCollapsedIdsDisplayed.length">
                            <div class="cgraph-section-lbl">
                                Collapsed IDs ({{ bundleCollapsedIdsDisplayed.length }}<template v-if="!bundlePanelShowAll && detailNode.bundleHiddenNotListed > 0"> of {{ (detailNode.bundleHiddenAllIds || []).length }}</template>)
                            </div>
                            <div class="cgraph-id-list">
                                <div v-for="(id, i) in bundleCollapsedIdsDisplayed" :key="'bi-'+i" class="cgraph-id-row">{{ id }}</div>
                            </div>
                        </template>
                    </template>

                    <!-- Entity (curation) -->
                    <template v-else-if="curationGraph && detailNode.type === 'entity'">
                        <div class="cgraph-section-lbl">Entity ID</div>
                        <div class="cgraph-mono-val">{{ detailNode.key }}</div>
                        <div class="cgraph-flag-row">
                            <span v-if="detailNode.inBothClusters" class="cgraph-flag cgraph-flag--amber"><i class="fa fa-code-branch me-1"></i>In both clusters</span>
                            <span v-if="detailNode.mergePreviewNew" class="cgraph-flag cgraph-flag--green"><i class="fa fa-plus me-1"></i>New from cluster 2</span>
                        </div>
                    </template>

                    <!-- Default -->
                    <template v-else>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">ID</span><span class="cgraph-mono-val small">{{ detailNode.id }}</span></div>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">Type</span><span class="cgraph-kv-val">{{ labels[detailNode.type] || detailNode.type }}</span></div>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">Domain</span><span class="cgraph-kv-val">{{ detailNode.domain }}</span></div>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">Key</span><span class="cgraph-mono-val small">{{ detailNode.key }}</span></div>
                        <div v-if="detailNode.entityCount != null" class="cgraph-kv"><span class="cgraph-kv-lbl">Entities in list</span><span class="cgraph-kv-val fw-bold">{{ detailNode.entityCount }}</span></div>
                        <div class="cgraph-flag-row">
                            <span v-if="detailNode.inBothClusters" class="cgraph-flag cgraph-flag--amber">In both cluster lists</span>
                        </div>
                    </template>

                    <!-- Cluster edges for node types that have no per-id list of their own
                         (entity, collapsed bundle). entity_group renders them inline above,
                         against the id each one belongs to. -->
                    <template v-if="detailNode.type !== 'entity_group' && (detailNode.clusterEdgeDetails || []).length">
                        <div class="cgraph-section-lbl">Cluster edges ({{ detailNode.clusterEdgeCount }})</div>
                        <div class="cgraph-id-list">
                            <div
                                v-for="(ce, ci) in detailNode.clusterEdgeDetails"
                                :key="'cle-' + ci"
                                class="cgraph-id-row cgraph-edge-row"
                            >
                                <span class="cgraph-edge-from">{{ ce.fromId }}</span>
                                <i class="fa fa-arrows-left-right cgraph-edge-arrow" title="Bidirectional"></i>
                                <span v-if="ce.pairLabel" class="cgraph-edge-pair">{{ ce.pairLabel }}</span>
                                <span class="cgraph-edge-to">{{ ce.ids.join(', ') }}</span>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import * as d3 from "d3";

/** Below this many entries the legend always fits one row, so the wrap toggle is pointless. */
const LEGEND_WRAP_TOGGLE_MIN = 5;

/** Ids rendered in the node panel before the "show all" toggle appears. */
const PANEL_ID_PAGE = 60;

/* Per-type fill colors — categorical palette, hue-spaced, no reds for data types */
const NODE_COLORS = {
    // Structural nodes — two distinct hub anchors; must NOT overlap data-type colors
    cluster:       "#6f42c1",  // purple-600   cluster hub 1  (left side in merge/split)
    cluster2:      "#0e7490",  // cyan-700     cluster hub 2  (right side in merge)

    // Semantic types — applied via neighborType prefix (e.g. "prsn~domain")
    prsn:          "#1d4ed8",  // blue-700     person / human identity
    cookie:        "#b45309",  // amber-700    browser cookie
    ck:            "#b45309",  // alias for cookie
    statid:        "#0f766e",  // teal-700     statistical identifier
    ip:            "#6d28d9",  // violet-700   IP address
    hh:            "#be185d",  // pink-700     household (warm, NOT error-red)
    org:           "#c2410c",  // orange-700   organization
    c_type:        "#0369a1",  // sky-700      configuration type
    hip:           "#7e22ce",  // purple-800   HIP biometric fingerprint
};

const NODE_LABELS = {
    prsn: "Person", cookie: "Cookie", ck: "Cookie",
    statid: "Stat ID", ip: "IP Address", hh: "Household",
    org: "Organization", c_type: "C Type", hip: "HIP",
    cluster: "Cluster", entity: "Entity",
    entity_group: "Neighbor Group", entity_bundle: "Collapsed",
};

function gcd(a, b) { return b === 0 ? a : gcd(b, a % b); }

/**
 * Categorical colour for one of `total` type~domain keys.
 *
 * This replaced a golden-angle generator (`hue = i × 137.508°`). The golden angle spreads well in
 * the limit, but at the ~10 types a real recommendation carries it still lands pairs 20° apart —
 * hue 138 and hue 158 at one fixed lightness are both simply "green", which is what made two
 * different `ck~…` groups look like the same node.
 *
 * Two changes fix it. Hues are spread evenly over however many types this graph actually has, so
 * the worst-case separation is 360/n rather than 20°. And the hue is not taken in sorted order:
 * keys that sort next to each other share a prefix — every `ck~…` domain sits together — so
 * consecutive hues would put the whole family in one narrow band. Walking the wheel in a stride
 * coprime with n scatters those neighbours to opposite sides of it.
 *
 * Lightness alternates as a second axis, so even two hues that do land near each other differ in
 * value and stay apart in greyscale and under colour-vision deficiency.
 */
function categoricalColor(sortedIndex, total) {
    const n = Math.max(1, total);
    /* ~0.382 n (golden ratio conjugate) walks the wheel in large steps; the coprime search keeps
       the walk a full cycle so no two keys collide. */
    let step = Math.max(1, Math.round(n * 0.382));
    while (step < n && gcd(step, n) !== 1) step++;
    if (gcd(step, n) !== 1) step = 1;

    const hue = (((sortedIndex * step) % n) * (360 / n) + 12) % 360;
    const even = sortedIndex % 2 === 0;
    return `hsl(${hue.toFixed(1)}, ${even ? 68 : 54}%, ${even ? 52 : 66}%)`;
}

/* Font Awesome 6 Solid unicode points */
const NODE_ICONS = {
    prsn:          "",   /* user          */
    cookie:        "",   /* cookie        */
    ck:            "",
    statid:        "",   /* id-card       */
    ip:            "",   /* network-wired */
    hh:            "",   /* house         */
    org:           "",   /* building      */
    c_type:        "",   /* sliders       */
    hip:           "",   /* fingerprint   */
    cluster:       "",   /* users         */
    entity:        "",   /* fingerprint   */
    entity_group:  "",   /* layer-group   */
    entity_bundle: "",   /* database      */
};

const FA_CLASS_MAP = {
    prsn: "fa fa-user", cookie: "fa fa-cookie", ck: "fa fa-cookie",
    statid: "fa fa-id-card", ip: "fa fa-network-wired",
    hh: "fa fa-house", org: "fa fa-building", c_type: "fa fa-sliders",
    hip: "fa fa-fingerprint", cluster: "fa fa-users",
    entity: "fa fa-fingerprint", entity_group: "fa fa-layer-group",
    entity_bundle: "fa fa-database",
};

/**
 * Link kinds that annotate a relationship rather than cluster membership. They are drawn but
 * excluded from the force layout, from cluster-membership lookups, and from lane assignment.
 */
const ANNOTATION_LINK_KINDS = new Set(["bridge", "cluster_edge"]);

/**
 * The cross-cluster line encoding. There is now exactly one — `connecting_cluster_edges`:
 *
 *   cluster_edge  rose dots,  1.5–2.6px, arrowheads both ends — an edge that exists, joining a
 *                 slice of one type~domain group to the slice of the group it connects to
 *   c1/c2         solid,      1.8px, cool — plain cluster membership (the skeleton), most recessive
 *
 * The amber 'connects' layer that used to share this canvas is gone with `connecting_entity_ids`.
 * Two cross-cluster encodings meaning different things — "is joined to" and "could have belonged
 * to" — read as one contradictory story, and only the first is an edge in the graph.
 *
 * Rose steps to a lighter, thinner tint once crowded (see clusterEdgeWeight): real data yields a
 * near-complete bipartite set — dozens of lines over ~30 nodes — and a weight that reads well for
 * a handful becomes a wall at that count.
 */
const CLUSTER_EDGE_LINK_KIND = "cluster_edge";
const CLUSTER_EDGE_COLOR = { light: "#be185d", dark: "#f43f5e" };
/** Crowded graphs: a lighter tint so the membership skeleton stays readable through them. */
const CLUSTER_EDGE_COLOR_DENSE = { light: "#f472b6", dark: "#f472b6" };
/**
 * Round-capped near-zero dashes render as true dots rather than short dashes, and the cap makes
 * dot diameter track stroke width — so the heavier cluster-edge line also gets visibly bigger dots.
 */
const CLUSTER_EDGE_DASH = "0.1 4.5";

/**
 * Tether from an opened type~domain node to the connecting-edge slices fanned out of it. Solid and
 * thin in the parent's own membership colour: it is not a graph edge and must not read as one, but
 * it also is not cross-cluster, so it stays out of the rose encoding entirely.
 */
const EXPAND_LINK_KIND = "expand";
/** Sub-node size and fan geometry, relative to the parent they belong to. */
const SUB_NODE_SCALE = 0.54;
/**
 * Radial gap between the outermost child ring and the ring the slices are seated on. The lanes
 * reserve this space whether or not anything is open, so a fan never has to share a radius with
 * nodes — placing slices in the band between hub and arc put them on top of the children there,
 * and an overlapping slice swallows the click meant for the node underneath it.
 */
const SLICE_RING_GAP = 96;
/** Arc spacing between sibling slices, in px at the slice ring's radius. */
const SUB_ROW_GAP = 62;
/* Radial step between rows of exploded ids, once one row cannot hold them all. */
const SUB_ROW_STEP = SUB_ROW_GAP + 26;
/**
 * Ladder: the layout when a node is open together with its partners across the lanes. Each side's
 * ids form a column facing the other, and a joined pair shares a row, so every line runs straight
 * across instead of two fans colliding in the gap. Row height clears the id label above the line
 * and the merge pill below it.
 */
const LADDER_ROW = 46;
/* Column offset from its parent, towards the other side: clears the parent's ring and label. */
const LADDER_INSET = 78;
/* Least distance between the two columns: two id labels facing each other along one line. */
const LADDER_MIN_GAP = 240;
/**
 * How many ids one group explodes into.
 *
 * Exploding every id of a large group would put hundreds of nodes on the canvas and nothing would
 * be readable, so the ids with edges come first and the rest are counted in the panel instead —
 * the point of the explosion is seeing which ids reach the other cluster.
 */
const MAX_EXPLODED_IDS = 24;
/**
 * The ladder's row budget: how many joined ids each column shows, raised a step at a time by the
 * "show more" control under it. Capped because every row is a node, a label and a pill — past a
 * hundred or so the fit shrinks the ladder below legibility, and the side panel lists every id
 * anyway. The partner budget is shared across all partners, so one id joined to five groups cannot
 * grow the partner column to five times the primary's.
 */
const LADDER_CAP_STEP = MAX_EXPLODED_IDS;
const LADDER_CAP_MAX = MAX_EXPLODED_IDS * 5;

let _uidCounter = 0;

export default {
    name: "D3CurationGraph",
    props: {
        data:            { type: Object,  default: null },
        nodeColors:      { type: Object,  default: () => ({}) },
        nodeLabels:      { type: Object,  default: () => ({}) },
        showBridgeStats: { type: Boolean, default: false },
        /**
         * Merge curation: entity-to-entity edges named by `connecting_cluster_edges`. Counted at
         * the id level, the same unit the node inspector reports, so the two never disagree — a
         * single drawn line can carry several of them when its endpoint nodes hold several ids.
         */
        clusterEdgeCount: { type: Number,  default: 0 },
        curationGraph:   { type: Boolean, default: true },
        enableNodeMove:  { type: Boolean, default: false },
        /** ID of the entity_group node currently armed for partial migration (amber ring) */
        highlightNodeId: { type: String,  default: null },
        /** nodeId → entity ids already staged to move, so sub-nodes can show as merged. */
        mergedEntityIds: { type: Object,  default: () => ({}) },
    },
    emits: ["graph-updated", "node-moved", "group-node-clicked", "merge-connection"],
    data() {
        return {
            hasGraph: false,
            isFullscreen: false,
            graphStats: { nodes: 0, edges: 0, sharedEntities: 0, collapsedEntities: 0, drawnClusterEdges: 0 },
            legendItems: [],
            legendExpanded: false,
            hiddenTypes: new Set(),
            bundlePanelShowAll: false,
            panelIdsShowAll: false,
            /**
             * Panel placement. These have to be declared here, not just assigned in a method:
             * undeclared, `this.panelPos = ...` lands on the instance without being reactive, so
             * the drag handler ran, the offset updated, and the panel never moved.
             *
             * It overlays the canvas rather than docking below it — a docked strip took its height
             * out of the flex column, and the graph is the one thing the card exists to show. To
             * keep an overlay from disturbing that, it is placed into the emptiest corner instead
             * of a fixed one, and can be dragged anywhere from there.
             */
            panelCollapsed: false,
            panelSide: "right",
            /** {x, y} in wrap coords — auto-chosen, or wherever the user dropped it. */
            panelPos: null,
            panelUserMoved: false,
            /** Parent node ids currently fanned out into connecting-edge sub-nodes. */
            expandedNodeIds: [],
            /** The node the curator clicked; the rest of `expandedNodeIds` opened with it as its partners. */
            primaryExpandedNodeId: null,
            /** Rows per ladder column; reset whenever a different node opens. */
            ladderCap: MAX_EXPLODED_IDS,
            /** parent id → ids actually drawn as slices, for the panel's "showing N of M". */
            explodedCounts: {},
            graphInspector: { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] },
            graphInspectorHideTimer: null,
            detailNode: null,
            simulation: null,
            svg: null,
            zoomBehavior: null,
            graphNodes: [],
            graphEdges: [],
            graphUid: `cg${++_uidCounter}`,
            prefixColorMap: new Map(),   // neighborType-prefix → palette color, assigned per-graph
        };
    },
    computed: {
        /**
         * Every entry is always rendered. The legend is one horizontally scrollable row by default
         * — with these type~domain names it used to wrap to four rows and cost the canvas ~140px,
         * which is the wrong trade in a card whose whole job is the graph. The toggle switches to
         * a wrapped view for scanning them all at once.
         */
        visibleLegendItems() {
            const items = this.legendItems.filter(i => !i.legendOverflow);
            if (items.length < LEGEND_WRAP_TOGGLE_MIN) return items;
            return items.concat([{
                type: "__wrap__",
                legendOverflow: true,
                label: this.legendExpanded ? "One row" : `All ${items.length}`,
            }]);
        },
        colors() { return { ...NODE_COLORS, ...this.nodeColors }; },

        /**
         * Cluster edges are near-complete bipartite in real data — 14 entities on one side each
         * joined to the same 5 on the other is 76 lines over ~30 nodes. A weight that reads well
         * for a handful becomes a wall at that count, so past 20 the layer steps down to a lighter
         * tint and a thinner stroke: still legible as a group, no longer covering the amber.
         */
        clusterEdgeWeight() {
            const n = this.graphEdges.filter(e => e.linkKind === CLUSTER_EDGE_LINK_KIND).length;
            const dense = n > 20;
            const palette = dense ? CLUSTER_EDGE_COLOR_DENSE : CLUSTER_EDGE_COLOR;
            return {
                color: this.isDark ? palette.dark : palette.light,
                width: dense ? (n > 60 ? 1.5 : 1.8) : 2.6,
                opacity: dense ? (n > 60 ? 0.55 : 0.7) : 0.9,
            };
        },

        laneHeaders() {
            const hubs = this.graphNodes.filter(d => d.type === "cluster");
            if (hubs.length !== 2) return [];
            const byIndex = [1, 2].map(i => hubs.find(h => Number(h.clusterIndex) === i));
            if (byIndex.some(h => !h)) return [];
            return byIndex.map((h, i) => ({
                side: i === 0 ? "left" : "right",
                label: this.getTypeLabel(h),
                full: h.clusterFullId || h.key || "",
                color: this.nodeFill(h),
            }));
        },
        labels() { return { ...NODE_LABELS, ...this.nodeLabels }; },
        isDark() { return this.$store.state.darkMode; },
        bundleCollapsedIdsDisplayed() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_bundle") return [];
            const all = d.bundleHiddenAllIds;
            const prev = d.bundleHiddenPreview || [];
            if (Array.isArray(all) && all.length && this.bundlePanelShowAll) return all;
            return prev;
        },
        entityGroupDetailIds() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_group") return [];
            if (d.entityIds && d.entityIds.length) return d.entityIds.map(String).filter(Boolean);
            const subs = d.entitySubgroups || [];
            const flat = subs.flatMap(sg => (sg.entityIds || []).map(String)).filter(Boolean);
            return [...new Set(flat)];
        },
        /**
         * One row per id in the open entity_group node: the id, how many cluster edges it has, and
         * those edges split by the type~domain group on the other side.
         *
         * `clusterEdgeDetails` is already keyed by the id an edge leaves from, so the grouping is
         * exact rather than inferred. Most-connected first — the ids that decide a merge should be
         * the ones at the top, not buried in a list sorted by whatever order the payload used.
         */
        entityGroupIdRows() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_group") return [];
            const byId = new Map();
            for (const ce of d.clusterEdgeDetails || []) {
                const k = String(ce.fromId);
                let groups = byId.get(k);
                if (!groups) { groups = []; byId.set(k, groups); }
                groups.push({ pairLabel: ce.pairLabel, ids: ce.ids.map(String) });
            }
            return this.entityGroupDetailIds
                .map(id => {
                    const groups = byId.get(String(id)) || [];
                    groups.sort((a, b) => b.ids.length - a.ids.length || String(a.pairLabel).localeCompare(String(b.pairLabel)));
                    return { id, groups, total: groups.reduce((sum, g) => sum + g.ids.length, 0) };
                })
                .sort((a, b) => b.total - a.total || String(a.id).localeCompare(String(b.id)));
        },
        /** How many of the group's ids are listed in the panel but not drawn on the canvas. */
        explodedOverflowCount() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_group" || d.isRelationSubNode) return 0;
            const shown = this.explodedCounts[d.id] != null ? this.explodedCounts[d.id] : this.explodedIds(d).length;
            return Math.max(0, (d.entityIds || []).length - shown);
        },
        /** Long groups render lazily — each row can expand, so the DOM cost is not just one line. */
        visibleEntityGroupIdRows() {
            const rows = this.entityGroupIdRows;
            return this.panelIdsShowAll ? rows : rows.slice(0, PANEL_ID_PAGE);
        },
    },
    watch: {
        detailNode(node) {
            this.bundlePanelShowAll = false;
            this.panelIdsShowAll = false;
            this.panelCollapsed = false;
            /* A panel the user has dragged stays where they put it — they chose. Otherwise it is
               re-placed for the new node, which is the whole point: the old fixed corner landed
               on the group that had just been opened. */
            if (!this.panelUserMoved) {
                this.panelPos = null;
                this.panelSide = this._sideAwayFrom(node);
                if (node) this.$nextTick(() => this._placePanelIfAuto());
            }
        },
        data: {
            handler(val) {
                if (val && val.nodes && val.edges) this.$nextTick(() => this.loadGraph(val));
            },
            deep: true,
        },
        isDark() {
            if (this.graphNodes.length > 0) this.$nextTick(() => this.drawGraph());
        },
        highlightNodeId(newId, oldId) {
            if (!this.svg) return;
            const g = this.svg.select("g");
            // Remove ring from previously highlighted node
            if (oldId) {
                g.selectAll(".cgn").filter(d => d.id === oldId)
                    .select(".cgn-circle")
                    .transition().duration(200)
                    .attr("stroke",       d => this.nodeCircleStroke(d).color)
                    .attr("stroke-width", d => this.nodeCircleStroke(d).width);
            }
            // Apply amber pulsing ring to new highlighted node
            if (newId) {
                g.selectAll(".cgn").filter(d => d.id === newId)
                    .select(".cgn-circle")
                    .attr("stroke", "#fbbf24")
                    .attr("stroke-width", 7);
            }
        },
    },
    mounted() {
        document.querySelectorAll(".cgraph-float-tip").forEach(el => el.remove());
        if (this.data && this.data.nodes && this.data.edges) {
            this.$nextTick(() => this.loadGraph(this.data));
        }
        if (typeof ResizeObserver !== "undefined" && this.$refs.graphContainer) {
            let _t = null;
            this._ro = new ResizeObserver(() => {
                clearTimeout(_t);
                _t = setTimeout(() => {
                    if (!this.svg || !this.hasGraph) return;
                    /* Laid out for a guessed width: re-frame is not enough, the slots are wrong. */
                    if (this._drewAtFallbackWidth && this.$refs.graphContainer
                        && this.$refs.graphContainer.clientWidth > 0) {
                        this.drawGraph();
                        return;
                    }
                    this.fitToScreen();
                }, 120);
            });
            this._ro.observe(this.$refs.graphContainer);
        }
    },
    beforeUnmount() {
        if (this.simulation) this.simulation.stop();
        this._clearInspTimer();
        if (this._ro) { this._ro.disconnect(); this._ro = null; }
        clearTimeout(this._fitTimer);
        document.querySelectorAll(".cgraph-float-tip").forEach(el => el.remove());
    },
    methods: {
        /* ── Color / icon helpers ─────────────────────────────────────── */
        getColor(type) { return this.colors[type] || "#64748b"; },

        getNodeIcon(d) {
            /* Extract the entity type prefix from neighborType (e.g. "prsn~aqfer.com" → "prsn") */
            if (d.neighborType) {
                const prefix = String(d.neighborType).split("~")[0].trim().toLowerCase();
                if (NODE_ICONS[prefix]) return NODE_ICONS[prefix];
            }
            return NODE_ICONS[d.type] || '';
        },

        /**
         * Shorten a `type~domain` key for a node label or legend chip.
         *
         * Real domains share long prefixes — `ck~c900_z__z_neutralfeed.com_z_aqferPage_default…`
         * and `ck~c900_z__z_neutralfeed.com_z_aqferBcnLcy_z_` differ only at the tail. Cutting the
         * end (the obvious approach) renders every one of them identically, so the type is kept
         * whole, a short head of the domain is kept for orientation, and the rest of the budget
         * goes to the tail that actually tells them apart.
         */
        shortPairLabel(raw, max) {
            const s = String(raw || "").trim();
            if (!s || s.length <= max) return s;
            const tilde = s.indexOf("~");
            if (tilde < 0) return `${s.slice(0, Math.max(1, max - 1))}…`;
            const type = s.slice(0, tilde);
            const domain = s.slice(tilde + 1);
            /* Budget left for the domain once "type~" and the ellipsis are paid for. */
            const room = max - type.length - 2;
            if (room < 6) return `${type}~…${domain.slice(-Math.max(3, room))}`;
            const head = Math.min(6, Math.floor(room / 3));
            return `${type}~${domain.slice(0, head)}…${domain.slice(-(room - head))}`;
        },

        getTypeLabel(d) {
            if (d.type === "cluster") {
                if (this.curationGraph) {
                    if (d.isSplitNew) {
                        const hash = d.clusterFullId || d.key || "";
                        return hash ? `Cluster ${hash.slice(0, 8)}` : `New Cluster ${Number(d.clusterIndex) - 1}`;
                    }
                    return d.clusterIndex != null ? `Cluster ${d.clusterIndex}` : "Cluster";
                }
                return this.labels.cluster || "Cluster";
            }
            /* A slice is labelled by the connection that defines it, not by its parent's
               type~domain — the parent sits right beside it already showing that. */
            if (d.isRelationSubNode) {
                const t = d.relationTargets || [];
                /* Unconnected ids are labelled with the id alone — see _nodeLabelLines. */
                if (!t.length) return "";
                const arrow = "\u2192";
                const edges = Number(d.clusterEdgeCount) || 0;
                const tail = edges ? ` \u00b7 ${edges}e` : "";
                /* One target is worth naming; several are not worth the width. */
                if (t.length === 1) return `${arrow} ${this.shortPairLabel(t[0], 18)}${tail}`;
                return `${arrow} ${t.length} groups${tail}`;
            }
            /* A cluster-edge endpoint is a lone entity split out of its type~domain group, so it
               keeps the group's label — "Entity" would lose the only context it has. */
            if (this.curationGraph && d.neighborType && (d.type === "entity_group" || d.type === "entity")) {
                return this.shortPairLabel(d.neighborType || d.key || "", 26) || "Group";
            }
            return this.labels[d.type] || d.type || "";
        },

        getKeyLabel(d, isLarge) {
            const max = isLarge ? 8 : 12;
            if (d.type === "cluster") {
                const cnt = Number(d.hubConnectionCount) || 0;
                return `${cnt} nodes`;
            }
            if (d.isRelationSubNode) {
                /* Empty: the slice says everything on its single label line above, and the id count
                   is inside its circle. A second line here collided with the merge pill. */
                return "";
            }
            if (d.type === "entity_group") {
                const n = this._entityGroupIdCount(d);
                /* Says what opening it gives you: how many ids, and how many of them are joined
                   to the other cluster. "3 groups" described buckets that are no longer drawn. */
                const linked = this._linkedIdCount(d);
                if (linked) return `${n} ids · ${linked} linked`;
                return `${n} ids`;
            }
            if (d.type === "entity_bundle") {
                const n = Number(d.bundleTotal);
                return Number.isFinite(n) ? `${n} total` : "";
            }
            const k = String(d.key || "").trim();
            if (!k) return "";
            return k.length <= max ? k : `…${k.slice(-(max - 1))}`;
        },

        /**
         * The canvas label for a node: the entity type as a kicker, then the part of the domain
         * that actually tells it apart.
         *
         * Splitting the raw `type~domain` at a fixed width put the same seventeen characters on
         * every node of a family — all four `ck~c900_z__z_neutralfeed…` groups led with
         * `ck~c900_z__z_neut`, so the widest, most prominent line carried nothing and the nodes read
         * as copies of each other. These names differ at the end, so the end is what gets the
         * space: the type moves to a small muted line above, and the tail becomes the identifier.
         *
         * The tail is capped at 17 characters for width, not for brevity — a wider label reaches
         * the node beside it, which is the overlap the arc spacing is sized against.
         *
         * @returns {[string, string]} `[type, identifier]`; the identifier is '' when the whole
         *   label already fits on one line.
         */
        _nodeLabelLines(d) {
            if (d.type === "cluster") return [this.getTypeLabel(d), ""];
            /* An exploded node IS an id, so the id is what it is labelled with — the team asked
               for the ids and got "no edge" on every one of them, because the label still
               described the edge bucket the node used to stand for. What it reaches goes on the
               quiet line above; when it reaches nothing there is no line, just the id. */
            if (d.isRelationSubNode) {
                const id = this._shortEntityId(d.entityId != null ? d.entityId : d.relationKey);
                const t = d.relationTargets || [];
                if (!t.length) return [id, ""];
                return [this.getTypeLabel(d), id];
            }
            const raw = String(d.neighborType || d.key || "");
            if (!raw) return [this.getTypeLabel(d), ""];

            const MAX = 17;
            const cut = raw.indexOf("~");
            if (cut < 0) {
                return raw.length <= MAX ? [raw, ""] : ["", "\u2026" + raw.slice(-(MAX - 1))];
            }
            const type = raw.slice(0, cut);
            const domain = raw.slice(cut + 1);
            /* Short enough to read whole: no reason to split it. */
            if (raw.length <= MAX) return [raw, ""];
            const tail = domain.length <= MAX ? domain : "\u2026" + domain.slice(-(MAX - 1));
            return [type, tail];
        },

        /**
         * How many of a group's ids reach the other cluster.
         *
         * Derived from the edge detail rather than read from `clusterEdgeEntityIds`: the detail is
         * the field every path sets, and a count that quietly reads 0 would drop the ring and the
         * chip off a node that does have connections.
         */
        _linkedIdCount(d) {
            if (!d) return 0;
            const details = d.clusterEdgeDetails || [];
            if (details.length) return new Set(details.map(x => String(x.fromId))).size;
            return (d.clusterEdgeEntityIds || []).length;
        },

        /** Entity ids are long and their tail is the distinguishing part. */
        _shortEntityId(id) {
            const s = String(id == null ? "" : id);
            const MAX = 14;
            return s.length <= MAX ? s : "\u2026" + s.slice(-(MAX - 1));
        },

        panelTitle(d) {
            if (!d) return "";
            if (this.curationGraph && d.type === "entity_group" && d.neighborType) return d.neighborType;
            if (this.curationGraph && d.type === "entity_bundle") return this.labels.entity_bundle || "Collapsed entities";
            return this.labels[d.type] || d.type;
        },

        panelIconClass(d) {
            return FA_CLASS_MAP[d.type] || "fa fa-circle";
        },

        /**
         * Where the panel sits decides whether the graph behind it is usable, so it is not given a
         * fixed corner. Docking it below the canvas was the other way out and it cost too much:
         * `.cgraph-canvas-area` is `flex: 1`, so a docked strip took its height straight out of
         * the graph. An overlay placed into free space keeps the full canvas and covers nothing.
         */
        panelStyle() {
            if (this.panelPos) {
                return {
                    left: `${this.panelPos.x}px`,
                    top: `${this.panelPos.y}px`,
                    right: "auto",
                    bottom: "auto",
                };
            }
            /* Before the first measurement: the side away from whatever is being inspected. */
            return { [this.panelSide]: "14px" };
        },

        /** Drag by the header, so the panel can always be moved off whatever it is covering. */
        startPanelDrag(ev) {
            if (ev.button != null && ev.button !== 0) return;
            const panel = ev.currentTarget && ev.currentTarget.closest(".cgraph-panel");
            const wrap = this.$refs.graphContainer && this.$refs.graphContainer.closest(".cgraph-wrap");
            if (!panel || !wrap) return;
            const pr = panel.getBoundingClientRect();
            const wr = wrap.getBoundingClientRect();
            const grabX = ev.clientX - pr.left;
            const grabY = ev.clientY - pr.top;

            const move = (e) => {
                /* Kept inside the canvas: a panel dragged off the edge cannot be dragged back. */
                const maxX = Math.max(0, wr.width - pr.width);
                const maxY = Math.max(0, wr.height - pr.height);
                this.panelUserMoved = true;
                this.panelPos = {
                    x: Math.max(0, Math.min(maxX, e.clientX - wr.left - grabX)),
                    y: Math.max(0, Math.min(maxY, e.clientY - wr.top - grabY)),
                };
            };
            const up = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
            };
            window.addEventListener("mousemove", move);
            window.addEventListener("mouseup", up);
            ev.preventDefault();
        },

        /** Hand placement back to the automatic choice, from wherever the user left it. */
        placePanelClear() {
            this.panelUserMoved = false;
            this.panelSide = this._sideAwayFrom(this.detailNode);
            this.panelPos = null;
            this.$nextTick(() => this._placePanelIfAuto());
        },

        _placePanelIfAuto() {
            if (!this.detailNode || this.panelUserMoved) return;
            this._autoPlacePanel();
        },

        /** Measures the panel and the canvas, then seats the panel in the emptiest slot. */
        _autoPlacePanel() {
            const c = this.$refs.graphContainer;
            const wrap = c && c.closest ? c.closest(".cgraph-wrap") : null;
            const panel = this.$el && this.$el.querySelector
                ? this.$el.querySelector(".cgraph-panel")
                : null;
            if (!c || !wrap || !panel) return;
            const pr = panel.getBoundingClientRect();
            const wr = wrap.getBoundingClientRect();
            if (!pr.width || !pr.height || !wr.width || !wr.height) return;
            const slot = this._bestPanelSlot({
                wrap: { w: wr.width, h: wr.height },
                panel: { w: pr.width, h: pr.height },
                boxes: this._panelObstacleBoxes(),
                prefer: this._sideAwayFrom(this.detailNode),
            });
            if (slot) this.panelPos = slot;
        },

        /**
         * The slot that hides the least. Cost is overlapped area, weighted, so the choice is not
         * "does it collide" — every slot collides on a busy graph — but "how much, and of what".
         *
         * Candidates are the four corners and the two side midpoints. Corners win on this layout
         * because the graph is two lanes at mid-height, which leaves the corners the emptiest part
         * of the canvas; the midpoints are there for the case where it does not.
         *
         * Ties keep the earlier candidate, so the ordering is the preference: the side away from
         * the node being inspected first, and top before bottom, since labels hang below a node.
         */
        _bestPanelSlot({ wrap, panel, boxes, gap = 14, prefer = "right" }) {
            if (!wrap || !panel) return null;
            const maxX = Math.max(gap, wrap.w - panel.w - gap);
            const maxY = Math.max(gap, wrap.h - panel.h - gap);
            const midY = Math.max(gap, Math.min(maxY, (wrap.h - panel.h) / 2));
            const near = prefer === "left" ? gap : maxX;
            const far = prefer === "left" ? maxX : gap;
            const candidates = [
                { x: near, y: gap },
                { x: near, y: maxY },
                { x: far, y: gap },
                { x: far, y: maxY },
                { x: near, y: midY },
                { x: far, y: midY },
            ];

            let best = null;
            for (const cand of candidates) {
                const x1 = cand.x + panel.w, y1 = cand.y + panel.h;
                let cost = 0;
                for (const b of boxes || []) {
                    const ox = Math.min(x1, b.x1) - Math.max(cand.x, b.x0);
                    const oy = Math.min(y1, b.y1) - Math.max(cand.y, b.y0);
                    if (ox > 0 && oy > 0) cost += ox * oy * (b.weight || 1);
                }
                if (!best || cost < best.cost) best = { cost, x: cand.x, y: cand.y };
                if (cost === 0) break;
            }
            return best ? { x: best.x, y: best.y } : null;
        },

        /**
         * What the panel must not cover, in wrap coordinates. Each node's full extent, not its
         * centre: it carries a circle, two label lines and — on a slice — a merge pill.
         *
         * Nodes the opened one actually points at are weighted heavily. They are the reason the
         * panel is open, so hiding one of those is far worse than hiding an idle node in a corner.
         */
        _panelObstacleBoxes() {
            const c = this.$refs.graphContainer;
            const wrap = c && c.closest ? c.closest(".cgraph-wrap") : null;
            const t = this._panelViewTransform();
            if (!c || !wrap || !t) return [];
            const cr = c.getBoundingClientRect();
            const wr = wrap.getBoundingClientRect();
            const dx = cr.left - wr.left, dy = cr.top - wr.top;
            const focus = this._focusIds;
            const boxes = [];
            this.graphNodes.forEach(n => {
                if (n.x == null || n.y == null) return;
                const r = this.nodeRadius(n);
                const side = Math.max(r, 66);
                const below = r + (n.isRelationSubNode ? 62 : 48);
                const sx = dx + t.tx + t.scale * n.x;
                const sy = dy + t.ty + t.scale * n.y;
                boxes.push({
                    x0: sx - side * t.scale,
                    x1: sx + side * t.scale,
                    y0: sy - (r + 12) * t.scale,
                    y1: sy + below * t.scale,
                    weight: focus && focus.has(n.id) ? 6 : 1,
                });
            });
            return boxes;
        },

        /**
         * The transform the nodes are about to be drawn under. A fit is animated, so during one
         * `__zoom` still holds the old view — placing against that would seat the panel for a
         * frame that is on its way out.
         */
        _panelViewTransform() {
            if (this._pendingFit) return this._pendingFit;
            const node = this.svg && this.svg.node ? this.svg.node() : null;
            const z = node && node.__zoom;
            if (z && typeof z.k === "number") return { tx: z.x, ty: z.y, scale: z.k };
            return this._fitTransform();
        },

        _sideAwayFrom(d) {
            /* Cluster 2 sits on the right, so its panel belongs on the left, and vice versa. */
            return this._clusterOf(d) === 2 ? "left" : "right";
        },

        emitMergeConnection(node, grp) {
            const parentId = node.isRelationSubNode ? node.parentNodeId : node.id;
            this.$emit("merge-connection", {
                nodeId: parentId,
                node: this.graphNodes.find(n => n.id === parentId) || node,
                entityIds: grp.ids.map(String),
                pairLabel: grp.targets.join(" + "),
            });
        },

        nodeCircleStroke(d) {
            if (d.pinned)           return { color: "#f59e0b", width: 3.5 };
            if (d.mergePreviewNew)  return { color: "#10b981", width: 3.5 };
            if (d.inBothClusters)   return { color: "#f59e0b", width: 3.5 };
            if (this.curationGraph && d.isMoved) {
                return { color: "#10b981", width: 4 };  // green: IDs that were migrated
            }
            if (this.curationGraph && d.type === "cluster" && d.isSplitNew) {
                return { color: "#f59e0b", width: 4 };
            }
            if (this.curationGraph && d.type === "entity_group") {
                if (d.nodeLinkKind === "c1") return { color: "#3b82f6", width: 3 };
                if (d.nodeLinkKind === "c2") return { color: "#10b981", width: 3 };
                if (d.nodeLinkKind === "c3") return { color: "#f43f5e", width: 3 };
                if (d.nodeLinkKind === "c4") return { color: "#f59e0b", width: 3 };
            }
            return { color: this.isDark ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.85)", width: 2.5 };
        },

        neighborTypeFill(d) {
            if (!d || d.neighborType == null) return null;
            const nt = String(d.neighborType).trim();
            if (!nt) return null;
            const ty = d.type;
            if (ty != null && ty !== "entity" && ty !== "entity_group") return null;
            /* Use full "type~domain" as key so each unique combination gets its own color */
            return this.prefixColorMap.get(nt.toLowerCase()) || null;
        },

        nodeFill(d) {
            if (d.isRelationSubNode) {
                /* The slice holding ids with no cross-cluster edge is inert — nothing to merge, no
                   line to trace — so it drops out of the palette entirely instead of competing
                   with the slices that carry a decision. */
                if (d.relationUnconnected) return this.isDark ? "#334155" : "#cbd5e1";
                /* Parent's hue, stepped toward the canvas: same family, clearly subordinate. */
                const base = this.nodeFill({ ...d, isRelationSubNode: false });
                return d3.interpolateRgb(base, this.isDark ? "#0b1220" : "#ffffff")(0.34);
            }
            /* Cluster hubs — distinct colors that never clash with data-type colors */
            if (d.type === "cluster") {
                const idx = Number(d.clusterIndex ?? 2);
                if (idx === 1) return this.colors["cluster"]  || "#6f42c1";
                if (idx === 2) return this.colors["cluster2"] || "#0e7490";
                // Additional split clusters get distinct warm/cool alternating colors
                const extras = ["#e11d48", "#d97706", "#059669", "#7c3aed", "#db2777", "#0891b2"];
                return extras[(idx - 3) % extras.length] || "#64748b";
            }
            if (d.neighborType) {
                /* Full "type~domain" key first → every unique combination gets its own color */
                const ntKey = String(d.neighborType).trim().toLowerCase();
                const byFull = this.prefixColorMap.get(ntKey);
                if (byFull) return byFull;
                /* Fallback: prefix semantic (node seen outside of current graph load) */
                const prefix = ntKey.split("~")[0];
                if (this.colors[prefix]) return this.colors[prefix];
            }
            /* No neighborType (entity, entity_bundle, unknown types) */
            const byType = this.prefixColorMap.get(d.type);
            if (byType) return byType;
            if (this.colors[d.type]) return this.colors[d.type];
            return "#64748b";
        },

        nodeRadius(d) {
            const base = 22;
            if (d.isRelationSubNode) {
                const nd = (d.entitySubgroups && d.entitySubgroups.length) || 1;
                return Math.round((base + Math.min(12, 2 + nd * 2)) * SUB_NODE_SCALE);
            }
            if (d.type === "cluster") {
                if (this.curationGraph) {
                    const c = Number(d.hubConnectionCount) || 0;
                    return base + Math.min(22, 8 + Math.floor(c / 5));
                }
                return base + Math.min(12, Math.floor((Number(d.entityCount) || 0) / 2));
            }
            if (d.type === "entity_group") {
                const nd = (d.entitySubgroups && d.entitySubgroups.length) || 1;
                let r = base + Math.min(12, 2 + nd * 2);
                if (this.curationGraph) {
                    const raw = String(d.neighborType || d.key || "");
                    const tl = (raw.indexOf("~") >= 0 ? raw.slice(0, raw.indexOf("~")) : raw).length;
                    r += Math.min(6, Math.floor(tl / 4) + 1);
                }
                return r;
            }
            if (d.type === "entity_bundle") {
                const ov = Number(d.bundleOverflow) || 0;
                return base + Math.min(14, 4 + Math.log2(ov + 2) * 2.5);
            }
            return d.inBothClusters ? base + 4 : base;
        },

        linkStroke(e) {
            const k = e.linkKind;
            if (k === EXPAND_LINK_KIND) return this.expandTetherStroke(e);
            if (k === "pair")   return this.isDark ? "#a78bfa" : "#8b5cf6";
            if (k === "c1")     return this.isDark ? "#93c5fd" : "#3b82f6";
            if (k === "c2")     return this.isDark ? "#6ee7b7" : "#10b981";
            if (k === "c3")     return this.isDark ? "#fda4af" : "#f43f5e";
            if (k === "c4")     return this.isDark ? "#fcd34d" : "#f59e0b";
            if (k === "c5")     return this.isDark ? "#6ee7b7" : "#059669";
            if (k === "c6")     return this.isDark ? "#c4b5fd" : "#7c3aed";
            /* rose — real cluster-joining edges from `connecting_cluster_edges` */
            if (ANNOTATION_LINK_KINDS.has(k)) return this.clusterEdgeWeight.color;
            return this.isDark ? "#2d3748" : "#e2e8f0";
        },

        /** Tether colour follows the side the opened node sits on, so the fan reads as its own. */
        expandTetherStroke(e) {
            const src = typeof e.source === "object" ? e.source : null;
            const kind = (src && src.nodeLinkKind) || "c1";
            return this.linkStroke({ linkKind: kind });
        },

        linkDash(e) {
            if (e.linkKind === "pair") return "6 4";
            if (e.linkKind === EXPAND_LINK_KIND) return "";
            if (ANNOTATION_LINK_KINDS.has(e.linkKind)) return CLUSTER_EDGE_DASH;
            return "";
        },

        /** Round caps turn the near-zero dashes of a cross-cluster line into dots. */
        linkCap(e) {
            return this.linkDash(e) === CLUSTER_EDGE_DASH ? "round" : "butt";
        },

        linkWidth(e) {
            if (e.linkKind === "pair") return 2.5;
            if (e.linkKind === EXPAND_LINK_KIND) return 1.4;
            if (ANNOTATION_LINK_KINDS.has(e.linkKind)) return this.clusterEdgeWeight.width;
            return 1.8;
        },

        linkOpacity(e) {
            if (e.linkKind === "pair") return 0.85;
            if (e.linkKind === EXPAND_LINK_KIND) return 0.5;
            if (ANNOTATION_LINK_KINDS.has(e.linkKind)) return this.clusterEdgeWeight.opacity;
            return 0.55;
        },

        /* ── Layout ───────────────────────────────────────────────────── */
        /** Which cluster (1 or 2) does a node belong to? 0 = neutral. */
        _clusterOf(d) {
            if (!d) return 0;
            if (d.type === "cluster") return Number(d.clusterIndex ?? 2) === 1 ? 1 : 2;
            /* entity_bundle nodes carry bundleLinkKind instead of nodeLinkKind — without it they
               read as neutral and get pulled to the centre, between the two hubs. */
            const lk = d.nodeLinkKind || d.bundleLinkKind;
            if (lk === "c1") return 1;
            if (lk === "c2") return 2;
            return 0;
        },

        /**
         * Assigns every hub and child a target slot (`tx`/`ty`) and returns the geometry the
         * simulation needs. Pure apart from those two fields, so the shape of the layout can be
         * asserted without a DOM.
         *
         * This replaced an adaptive force blob — charge scaled by node count, 60px collision
         * padding, a ring radius derived from that padding, hub separation of at least 55% of the
         * canvas. Those numbers compounded: 8 children gave a ring radius of ~270, which set the
         * lanes ~1400px apart while the nodes occupied ~350px of height. fitToScreen then had to
         * squeeze a 4:1 layout into a 2:1 canvas, so the graph shrank to an unreadable band with
         * dead space above and below it.
         *
         * Deriving the positions means the aspect ratio is chosen rather than emergent: children
         * sit on an arc whose radius is sized both to seat them at a legible gap and to fit the
         * canvas height, and the lane separation follows from that radius instead of from the
         * canvas width. Capacity comes from the arc's angle rather than its radius — at a fixed arc
         * length a wider span needs a smaller radius, which is what stops the layout outgrowing
         * the canvas as nodes are added.
         */
        _layoutTargets(nodes, W, H, edges) {
            const ARC_SPAN = 2.8;        // ~160°, centred on the axis facing the other cluster
            /* Clearance between any two children, along the arc AND between rings.

               It has to cover the worst direction, because the arc runs through every angle. A node
               occupies roughly 2·nr + 58 vertically (two label lines and a count line below, the
               next node's ring above) and half-label + nr + ring ≈ 96 horizontally with the wrapped
               two-line label. Governing only one direction is what let the earlier overlaps
               through: at 92 the arc gap was too tight, and after widening it the ring step was
               still 88 so inner and outer rings collided instead.

               A radial distance is not enough on its own. Two nodes offset diagonally can be 124
               apart and still overlap, because that splits into 80 across and 95 down — under both
               thresholds at once. The slot is therefore the diagonal of the two: √(96² + 114²). */
            const NODE_SLOT = 150;
            const MIN_CHILD_GAP = NODE_SLOT;
            const MIN_ARC_R = 150;

            const cx = W / 2;
            const cyMid = H / 2;

            /* Which hub each child hangs off.
 
               A child normally says so itself, but the merge-preview graph builds its nodes without
               a link kind. Those were skipped here, so they got no slot, and the `arrange` force
               skips anything with no `tx` — leaving the collision force alone to push them out of
               the canvas centre one by one, which is the diagonal cascade the preview showed.
 
               The membership edge is the answer when the node itself is silent: it names the hub.
               Only membership kinds are read — a cluster edge joins two children, and an annotation
               edge can touch a hub it does not belong to. */
            const hubs = nodes.filter(n => n.type === "cluster");
            const hubCluster = new Map(hubs.map(h => [h.id, this._clusterOf(h)]));
            const sideByEdge = new Map();
            for (const e of edges || []) {
                if (!/^c\d+$/.test(String(e.linkKind || ""))) continue;
                const sid = typeof e.source === "object" && e.source ? e.source.id : e.source;
                const tid = typeof e.target === "object" && e.target ? e.target.id : e.target;
                if (hubCluster.has(sid) && !hubCluster.has(tid)) sideByEdge.set(tid, hubCluster.get(sid));
                else if (hubCluster.has(tid) && !hubCluster.has(sid)) sideByEdge.set(sid, hubCluster.get(tid));
            }
            /* One hub means one home: nothing is ambiguous, so no child is left without a slot. */
            const onlyHub = hubs.length === 1 ? this._clusterOf(hubs[0]) : 0;
            const sideOf = n => this._clusterOf(n) || sideByEdge.get(n.id) || onlyHub;

            /* Children per hub, in a stable order so the arc does not reshuffle between redraws. */
            const childrenByCluster = new Map();
            for (const n of nodes) {
                if (n.type === "cluster" || n.isRelationSubNode) continue;
                const c = sideOf(n);
                if (!c) continue;
                if (!childrenByCluster.has(c)) childrenByCluster.set(c, []);
                childrenByCluster.get(c).push(n);
            }
            /* Merge preview folds both clusters into one hub. With no second lane to stay clear of,
               the ring can use the whole turn and the hub belongs in the middle of the canvas —
               a 160° arc off to one side wastes most of it. */
            const solo = hubs.length <= 1;
            for (const list of childrenByCluster.values()) {
                list.sort((a, b) =>
                    String(a.neighborType || a.key || a.id).localeCompare(String(b.neighborType || b.key || b.id)) ||
                    String(a.id).localeCompare(String(b.id))
                );
            }

            /* One ring per hub, never concentric.

               Concentric rings put a child between the hub and another child, and a membership edge
               is a straight radial line — so the outer node's spoke ran through the inner one and
               three siblings read as a chain. It was measured at 0.7px off the line. No offset
               between rings fixes it either: an outer ring holds more items, so its cells are finer,
               and whatever the stagger some outer angle lands within half a cell of an inner one.

               A single ring cannot produce the arrangement at all — nothing is ever between the hub
               and a child. Capacity comes from the arc's angle instead of a second ring: the span
               grows from the wedge facing the other cluster towards a full circle until the ring
               fits the height, which also frames better than concentric arcs did, since a wide arc
               uses the canvas in both directions. */
            const maxRingHeight = (H * 1.5 * 0.88) / 2;
            /* Capped at a half-turn: past it the ring wraps behind the hub, and those children push
               the layout wider than the canvas — the sprawl this whole approach exists to avoid. */
            const SPAN_STEPS = solo
                ? [ARC_SPAN, 3.0, Math.PI, 4.2, 5.2, 2 * Math.PI]
                : [ARC_SPAN, 3.0, Math.PI];
            const seatRing = (count) => {
                const n = Math.max(1, count);
                for (const span of SPAN_STEPS) {
                    const R = Math.max(MIN_ARC_R, (n * MIN_CHILD_GAP) / span);
                    if (2 * R * Math.sin(Math.min(span, Math.PI) / 2) <= maxRingHeight * 2) {
                        return { R, span };
                    }
                }
                /* Past the last span there is nowhere left to go, so the ring tightens instead —
                   density degrades, but no child ever ends up behind another. */
                const span = SPAN_STEPS[SPAN_STEPS.length - 1];
                const R = Math.max(MIN_ARC_R, (n * MIN_CHILD_GAP) / span);
                return { R: Math.min(R, maxRingHeight), span };
            };

            const ringByCluster = new Map();
            for (const [c, list] of childrenByCluster) ringByCluster.set(c, seatRing(list.length));

            let outerR = MIN_ARC_R;
            for (const r of ringByCluster.values()) outerR = Math.max(outerR, r.R);

            /* Each ring reaches `outerR` towards the centre, so the lane has to be at least that
               plus a gap, or cluster-1 children end up level with cluster-2 children. The gap has
               to clear two half-labels facing each other as well as the reserved slice ring. */
            const LANE_GAP = SLICE_RING_GAP + 56;

            /* When the canvas cannot afford that lane, the rings shrink — the lane does not.
               Capping the lane instead is what let the two sides interleave: on a narrow canvas the
               cap silently overrode the separation the radius had asked for, and the innermost
               children of the two clusters ended up with their labels on top of each other. Losing
               a little ring radius costs density; losing the lane costs the whole point of drawing
               two clusters side by side. */
            const maxLaneHalf = W * 0.46;
            let laneSqueeze = 1;
            if (outerR + LANE_GAP > maxLaneHalf) {
                const allowed = Math.max(MIN_ARC_R * 0.5, maxLaneHalf - LANE_GAP);
                laneSqueeze = allowed / outerR;
                outerR = allowed;
            }
            const laneHalf = solo ? 0 : Math.max(W * 0.14, outerR + LANE_GAP);
            const laneX = c => (laneHalf === 0 ? cx : c === 1 ? cx - laneHalf : c === 2 ? cx + laneHalf : cx);

            for (const h of nodes) {
                if (h.type !== "cluster") continue;
                h.tx = laneX(this._clusterOf(h));
                h.ty = cyMid;
            }
            for (const [c, list] of childrenByCluster) {
                const ring = ringByCluster.get(c);
                /* Whatever the lane took off the radius applies here too, or the ring would still
                   reach into the gap the lane just reserved. */
                const R = ring.R * laneSqueeze;
                const span = ring.span;
                const hubX = laneX(c);
                /* Centred on the axis facing the other cluster, so the nodes nearest it — the ones
                   a cross-cluster edge reaches first — stay closest to it. */
                const axis = solo ? -Math.PI / 2 : c === 2 ? Math.PI : 0;
                list.forEach((n, i) => {
                    const a = axis + span * ((i + 0.5) / list.length - 0.5);
                    n.tx = hubX + Math.cos(a) * R;
                    n.ty = cyMid + Math.sin(a) * R;
                });
            }

            /* `sliceR` is where an opened node's slices sit: outside every child ring, so the fan
               is guaranteed a clear radius whichever ring its parent happens to be on. */
            return {
                cx, cyMid, laneHalf, laneX, outerR,
                arcSpan: ARC_SPAN, sliceR: outerR + SLICE_RING_GAP,
            };
        },

        /**
         * Where one slice sits: on the reserved ring outside every child ring, at its parent's own
         * bearing from the hub, offset along the ring by its index.
         *
         * The ring is reserved because slices used to sit in the band between hub and arc, which is
         * only empty when nothing else is there — with children on an inner ring the slices landed
         * on top of them, and a slice covering a node takes the click meant for it.
         *
         * Pure, so the geometry can be asserted without a DOM.
         */
        _slicePosition(parent, cluster, slice, geom) {
            const sliceR = geom.sliceR;
            const hubX = geom.laneX(cluster);
            const axis = cluster === 2 ? Math.PI : 0;

            const dx = parent.x - hubX;
            const dy = parent.y - geom.cyMid;
            const bearing = (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001)
                ? axis
                : Math.atan2(dy, dx);

            const total = Math.max(1, Number(slice.relationSiblings) || 1);
            const i = Number(slice.relationIndex) || 0;
            /* Rows, because a group now explodes into every one of its ids rather than into a
               handful of buckets. Twenty ids on one arc spanned ±100°, well outside the wedge —
               they wrapped around the hub and over the other cluster's children. */
            const rows = this._sliceRows(total, geom);
            let row = rows[rows.length - 1];
            let seen = 0;
            for (const r of rows) {
                if (i < seen + r.take) { row = { ...r, index: i - seen }; break; }
                seen += r.take;
            }
            if (row.index == null) row = { ...row, index: Math.max(0, i - seen) };
            const rowR = row.R;
            const rowTotal = Math.max(1, row.take);
            /* Constant arc spacing, so the row reads evenly however far out the ring sits. */
            const step = SUB_ROW_GAP / Math.max(120, rowR);
            const half = ((rowTotal - 1) / 2) * step;

            /* The offset from the axis is wrapped into [-π, π] before it is clamped. Cluster 2's
               axis is π, so a parent just above it has a bearing near -2.7 and the raw difference
               came out at -5.8 — outside the wedge by a full turn rather than by any real angle.
               The clamp then pinned it to the wedge edge and dropped the whole fan a full radius
               below the hub, at the bottom of the canvas, nowhere near the node it belonged to. */
            const diff = bearing - axis;
            const wrapped = Math.atan2(Math.sin(diff), Math.cos(diff));
            const limit = geom.arcSpan / 2 + 0.25;
            /* When the row is wider than the wedge there is no offset that keeps it inside, so it
               is centred on the axis rather than pinned to an edge — pinning pushed the whole row
               off to one side and over the neighbouring lane. */
            const room = limit - half;
            const rel = room <= 0 ? 0 : Math.max(-room, Math.min(room, wrapped));
            const angle = axis + rel + (row.index - (rowTotal - 1) / 2) * step;

            return {
                x: hubX + Math.cos(angle) * rowR,
                y: geom.cyMid + Math.sin(angle) * rowR,
            };
        },

        /**
         * How the exploded ids divide into rows: as many as the wedge can hold at that radius,
         * then out to the next radius. Pure, and derived only from the count and the geometry, so
         * every slice of one parent computes the same rows without sharing state.
         */
        _sliceRows(total, geom) {
            const rows = [];
            let left = Math.max(1, total);
            let r = 0;
            while (left > 0 && r < 6) {
                const R = geom.sliceR + r * SUB_ROW_STEP;
                const cap = Math.max(1, Math.floor((geom.arcSpan * R) / SUB_ROW_GAP));
                const take = Math.min(left, cap);
                rows.push({ take, R, index: null });
                left -= take;
                r++;
            }
            /* Past six rows the fan would leave the canvas; the last row takes the remainder and
               tightens instead. The id cap makes this unreachable in practice. */
            if (left > 0) rows[rows.length - 1].take += left;
            return rows;
        },

        /* ── Node expansion (connecting-edge sub-nodes) ───────────────── */
        isNodeExpanded(id) { return this.expandedNodeIds.indexOf(String(id)) !== -1; },

        /**
         * A group explodes into its ids. Worth doing when there is more than one id, or when the
         * single id has an edge to reveal — a lone id joined to nothing has nothing to show.
         *
         * This used to require `relationGroups`, so a group whose ids all had the same edges — or
         * no edges at all — could not be opened. The explosion is per id now, so the question is
         * only whether there are ids.
         */
        canExpandNode(d) {
            if (!d || d.type !== "entity_group" || d.isRelationSubNode) return false;
            const n = (d.entityIds || []).length;
            return n > 1 || (n === 1 && !!d.hasClusterEdge);
        },

        /**
         * The ids a group explodes into, edge-carrying ones first so the cap keeps the ones the
         * curator opened the group to see. Order is otherwise the group's own.
         *
         * `rank` (id → number, lower first) reorders the edge-carrying ids; a partner uses it to
         * take the ids joined to the clicked node's rows, in row order. Without it the two sides
         * capped independently, kept different ids, and a join whose far id was cut had no slice
         * to land on.
         */
        explodedIds(d, rank, cap = MAX_EXPLODED_IDS) {
            const ids = (d.entityIds || []).map(String);
            /* Read off the edge detail rather than the precomputed id list: the detail is the
               field every code path sets, so the ordering cannot quietly stop working. */
            const linked = new Set((d.clusterEdgeDetails || []).map(x => String(x.fromId)));
            const withEdges = ids.filter(id => linked.has(id));
            if (rank) {
                const pos = new Map(withEdges.map((id, i) => [id, i]));
                withEdges.sort((a, b) => (rank(a) - rank(b)) || (pos.get(a) - pos.get(b)));
            }
            const without = ids.filter(id => !linked.has(id));
            return withEdges.concat(without).slice(0, cap);
        },

        /**
         * One connection open at a time: the clicked node plus the nodes its cross-cluster edges
         * reach, so both ends show their ids and each line runs id to id. Opening unrelated nodes
         * together put their slices into the same band of free space around a hub, where the
         * slices — and their labels, and their merge pills — landed on top of each other. The
         * partners sit across the other hub, so they fan into space of their own.
         *
         * Clicking the node that opened the set closes it; clicking any other node — a partner
         * included — replaces the set with that node's own.
         */
        toggleNodeExpanded(id) {
            const k = String(id);
            if (this.primaryExpandedNodeId === k) {
                this.expandedNodeIds = [];
                this.primaryExpandedNodeId = null;
            } else {
                this.expandedNodeIds = [k, ...this._crossClusterPartnerIds(k)];
                this.primaryExpandedNodeId = k;
            }
            this.ladderCap = MAX_EXPLODED_IDS;
            this._redrawFromBase();
        },

        /** The ladder's "show more": one more step of rows on each column, up to the cap. */
        showMoreLadderRows() {
            if (this.ladderCap >= LADDER_CAP_MAX) return;
            this.ladderCap = Math.min(LADDER_CAP_MAX, this.ladderCap + LADDER_CAP_STEP);
            this._redrawFromBase();
        },

        /** Expandable nodes joined to `id` by a cross-cluster edge in the unexpanded data. */
        _crossClusterPartnerIds(id) {
            const base = this._baseData;
            if (!base) return [];
            const idOf = v => (typeof v === "object" && v !== null ? v.id : v);
            const byId = new Map(base.nodes.map(n => [String(n.id), n]));
            const out = new Set();
            for (const e of base.edges) {
                if (!ANNOTATION_LINK_KINDS.has(e.linkKind)) continue;
                const src = String(idOf(e.source));
                const tgt = String(idOf(e.target));
                const other = src === id ? tgt : tgt === id ? src : null;
                if (other && other !== id && this.canExpandNode(byId.get(other))) out.add(other);
            }
            return [...out];
        },

        collapseAllNodes() {
            if (!this.expandedNodeIds.length) return;
            this.expandedNodeIds = [];
            this.primaryExpandedNodeId = null;
            this.ladderCap = MAX_EXPLODED_IDS;
            this._redrawFromBase();
        },

        /** Re-runs the pipeline from the untouched prop data, so expansion is never cumulative. */
        _redrawFromBase() {
            if (this._baseData) this.loadGraph(this._baseData);
        },

        /**
         * Decides what is actually on the canvas.
         *
         * Cross-cluster edges are hidden until the node they belong to is opened. Drawing all of
         * them by default put 76 rose lines over 17 nodes on a real recommendation — every node
         * ringed, every path crossing every other, and no way to tell which entity joined which.
         * The count stays visible in the toolbar so nothing is concealed; the lines arrive when
         * there is a node to attribute them to.
         *
         * Opening a node also fans it into one sub-node per connecting-edge group, and its edges
         * are re-pointed onto whichever sub-node owns them, so a revealed line runs from the exact
         * slice of ids that carries it.
         */
        _visibleGraph(inputData) {
            const idOf = v => (typeof v === "object" && v !== null ? v.id : v);
            const baseById = new Map(inputData.nodes.map(n => [n.id, n]));
            const expandedIds = new Set(
                this.expandedNodeIds.filter(id => this.canExpandNode(baseById.get(id)))
            );

            /* Only edges touching an opened node are drawn, and only the nodes at their far end
               get the rose ring — so the ring means "joined to what you opened", not "has edges
               somewhere", which was true of nearly every node and therefore said nothing. */
            const revealed = [];
            const farEndIds = new Set();
            /* Partners open only to show their end of the clicked node's lines. Their own lines to
               other nodes stay hidden, or clicking one group would draw a second group's edges. */
            const primary = expandedIds.has(this.primaryExpandedNodeId) ? this.primaryExpandedNodeId : null;
            for (const e of inputData.edges) {
                if (!ANNOTATION_LINK_KINDS.has(e.linkKind)) continue;
                const src = idOf(e.source);
                const tgt = idOf(e.target);
                if (primary && src !== primary && tgt !== primary) continue;
                const sIn = expandedIds.has(src);
                const tIn = expandedIds.has(tgt);
                if (!sIn && !tIn) continue;
                revealed.push(e);
                if (sIn) farEndIds.add(tgt);
                if (tIn) farEndIds.add(src);
            }

            /* The clicked node explodes first; the partners then share one budget of the same
               size, spent on the ids joined to the primary's rows in row order, so the two columns
               match and neither outgrows the other however many groups the primary reaches. */
            const exploded = new Map();
            const partnerIds = primary ? [...expandedIds].filter(id => id !== primary) : [];
            const cap = partnerIds.length ? this.ladderCap : MAX_EXPLODED_IDS;
            if (primary) {
                const primaryIds = this.explodedIds(baseById.get(primary), null, cap);
                exploded.set(primary, primaryIds);
                const primaryPos = new Map(primaryIds.map((id, i) => [id, i]));
                const primaryLabel = String(baseById.get(primary).neighborType || "");
                const candidates = [];
                partnerIds.forEach((pid, pi) => {
                    const n = baseById.get(pid);
                    const rankById = new Map();
                    for (const d of n.clusterEdgeDetails || []) {
                        if (String(d.pairLabel) !== primaryLabel) continue;
                        for (const to of d.ids || []) {
                            const r = primaryPos.get(String(to));
                            if (r == null) continue;
                            const k = String(d.fromId);
                            rankById.set(k, Math.min(rankById.has(k) ? rankById.get(k) : Infinity, r));
                        }
                    }
                    const rank = id => (rankById.has(id) ? rankById.get(id) : Infinity);
                    this.explodedIds(n, rank, Infinity).forEach((id, pos) => {
                        candidates.push({ pid, pi, id, pos, r: rank(id) });
                    });
                    exploded.set(pid, []);
                });
                candidates.sort((a, b) => (a.r - b.r) || (a.pi - b.pi) || (a.pos - b.pos));
                const taken = candidates.slice(0, cap);
                for (const c of taken) exploded.get(c.pid).push(c.id);

                /* A primary id joined only to partner ids the budget left out has nothing to
                   draw to, and on the canvas it read as joined to nothing — which it is not. It
                   waits for "show more" with the rest; ids that really join nothing stay. */
                const reached = new Set(taken.filter(c => Number.isFinite(c.r)).map(c => c.r));
                const joinsAPartner = new Set();
                for (const pid of partnerIds) {
                    const ids = new Set((baseById.get(pid).entityIds || []).map(String));
                    const label = String(baseById.get(pid).neighborType || "");
                    for (const d of baseById.get(primary).clusterEdgeDetails || []) {
                        if (String(d.pairLabel) === label && (d.ids || []).some(to => ids.has(String(to)))) {
                            joinsAPartner.add(String(d.fromId));
                        }
                    }
                }
                exploded.set(primary, primaryIds.filter((id, i) => reached.has(i) || !joinsAPartner.has(id)));
            }
            const explodeOf = n => {
                if (!exploded.has(n.id)) exploded.set(n.id, this.explodedIds(n));
                return exploded.get(n.id);
            };

            const nodes = [];
            const subNodeForId = new Map();
            for (const n of inputData.nodes) {
                /* An opened parent hands its edges to its slices, so the ring belongs on them. */
                const ring = farEndIds.has(n.id) && !expandedIds.has(n.id);
                nodes.push(ring === !!n.showEdgeRing ? n : { ...n, showEdgeRing: ring });

                if (!expandedIds.has(n.id)) continue;
                const merged = new Set((this.mergedEntityIds[n.id] || []).map(String));
                /* One sub-node per id, not per edge signature.
 
                   Grouping by signature meant a ten-id group opened into "3 groups", and a curator
                   asking "which ids reach the other cluster" had to read a bucket label to find
                   out. It also made a single id unmovable, because the node on the canvas stood
                   for several of them. One node per id answers the question directly and is the
                   unit a move already works in. */
                const ids = explodeOf(n);
                ids.forEach((eid, gi) => {
                    const subId = `${n.id}|id|${gi}`;
                    const slice = this._clusterEdgeSliceFor(n, [eid]);
                    const targets = [...new Set(
                        (slice.clusterEdgeDetails || []).map(d => String(d.pairLabel)).filter(Boolean)
                    )].sort();
                    nodes.push({
                        ...n,
                        id: subId,
                        /* Slices are parts of a node, not peers: no legend entry of their own, and
                           the parent's identity is carried so colour and label stay consistent. */
                        isRelationSubNode: true,
                        parentNodeId: n.id,
                        entityId: eid,
                        relationGroups: [],
                        relationTargets: targets,
                        relationKey: eid,
                        relationIndex: gi,
                        relationSiblings: ids.length,
                        relationUnconnected: targets.length === 0,
                        relationAllMerged: merged.has(String(eid)),
                        entityIds: [eid],
                        entitySubgroups: [{ neighborDomain: "", entityIds: [eid] }],
                        idCountInNode: 1,
                        showEdgeRing: !!slice.hasClusterEdge,
                        ...slice,
                    });
                    subNodeForId.set(`${n.id}|${eid}`, subId);
                });
            }

            const nodeById = new Map(nodes.map(n => [n.id, n]));
            /* Which slice of `parentId` carries the edges reaching `otherNodeId`. */
            const reroute = (parentId, otherNodeId) => {
                if (!expandedIds.has(parentId)) return parentId;
                const parent = nodeById.get(parentId);
                const other = nodeById.get(otherNodeId);
                const otherLabel = other ? String(other.neighborType || other.key || "") : "";
                for (const d of (parent && parent.clusterEdgeDetails) || []) {
                    if (d.pairLabel !== otherLabel) continue;
                    const sub = subNodeForId.get(`${parentId}|${d.fromId}`);
                    if (sub) return sub;
                }
                return parentId;
            };

            const edges = [];
            for (const e of inputData.edges) {
                /* Cross-cluster edges are added from `revealed` below, never wholesale. */
                if (!ANNOTATION_LINK_KINDS.has(e.linkKind)) edges.push(e);
            }
            for (const parentId of expandedIds) {
                const parent = baseById.get(parentId);
                explodeOf(parent).forEach((eid, gi) => {
                    edges.push({ source: parentId, target: `${parentId}|id|${gi}`, linkKind: EXPAND_LINK_KIND });
                });
            }
            /* Both ends open: one line per joined id pair, sub-node to sub-node, so the curator
               sees exactly which id meets which. An id past the explode cap has no sub-node and
               falls back to its parent. Otherwise one end is closed and the line runs from the
               open end's slice to the closed node, as before. */
            const drawnPairs = new Set();
            const pushPair = (e, a, b) => {
                const pk = a < b ? `${a}|${b}` : `${b}|${a}`;
                if (drawnPairs.has(pk)) return;
                drawnPairs.add(pk);
                edges.push({ ...e, source: a, target: b });
            };
            for (const e of revealed) {
                const src = idOf(e.source);
                const tgt = idOf(e.target);
                if (!(expandedIds.has(src) && expandedIds.has(tgt))) {
                    edges.push({ ...e, source: reroute(src, tgt), target: reroute(tgt, src) });
                    continue;
                }
                const srcNode = nodeById.get(src);
                const tgtNode = nodeById.get(tgt) || {};
                const tgtLabel = String(tgtNode.neighborType || tgtNode.key || "");
                const tgtIds = new Set((tgtNode.entityIds || []).map(String));
                for (const d of (srcNode && srcNode.clusterEdgeDetails) || []) {
                    if (String(d.pairLabel) !== tgtLabel) continue;
                    const a = subNodeForId.get(`${src}|${d.fromId}`) || src;
                    for (const toId of d.ids || []) {
                        /* The detail is keyed by label, not node: only ids that live in `tgt`. */
                        if (!tgtIds.has(String(toId))) continue;
                        const b = subNodeForId.get(`${tgt}|${toId}`) || tgt;
                        /* Both ends are open, so an end that fell back to its parent is an id past
                           the explode cap. A line to the parent would cross every row between; the
                           panel lists what the canvas leaves out. */
                        if (a === src || b === tgt) continue;
                        pushPair(e, a, b);
                    }
                }
            }

            if (primary && expandedIds.size > 1) this._assignLadderRows(nodes, edges, primary, expandedIds);

            /* What the open node concerns: itself, its slices, the nodes its lines reach, and both
               hubs for orientation. Everything else is dimmed rather than removed — the shape of
               the cluster stays legible, it just stops competing. */
            let focusIds = null;
            if (expandedIds.size) {
                focusIds = new Set(farEndIds);
                for (const id of expandedIds) focusIds.add(id);
                for (const n of nodes) {
                    if (n.isRelationSubNode && expandedIds.has(n.parentNodeId)) focusIds.add(n.id);
                    if (n.type === "cluster") focusIds.add(n.id);
                }
            }

            const counts = {};
            for (const id of expandedIds) counts[id] = explodeOf(baseById.get(id)).length;
            this.explodedCounts = counts;

            return { ...inputData, nodes, edges, revealedEdgeCount: revealed.length, focusIds };
        },

        /**
         * Seats the open slices on ladder rows (`ladderSide`, `ladderRow`, `ladderRows`).
         *
         * Walks the clicked node's ids in order (joined ids first). Each takes the next row, and
         * the partner ids joined to it — through the first primary id they are joined to — take
         * that row and the ones after it; the primary column skips the extra rows. So a one-to-one
         * pair is level, a one-to-many fans downward, and no two lines cross. Ids joined to
         * nothing, on either side, fill the rows after the last joined one — without a line or a
         * pill, sharing a row does not read as a pair. Mutates the slices, which are fresh per call.
         */
        _assignLadderRows(nodes, edges, primary, expandedIds) {
            const idOf = v => (typeof v === "object" && v !== null ? v.id : v);
            const primarySubs = nodes.filter(n => n.isRelationSubNode && n.parentNodeId === primary);
            const parentOrder = [...expandedIds].filter(id => id !== primary);
            const partnerSubs = nodes.filter(n => n.isRelationSubNode && n.parentNodeId !== primary
                && expandedIds.has(n.parentNodeId));
            if (!primarySubs.length || !partnerSubs.length) return;

            const rowOf = new Map(primarySubs.map((n, i) => [n.id, i]));
            const wanted = new Map();
            for (const e of edges) {
                if (!ANNOTATION_LINK_KINDS.has(e.linkKind)) continue;
                const a = idOf(e.source);
                const b = idOf(e.target);
                const [p, q] = rowOf.has(a) ? [a, b] : rowOf.has(b) ? [b, a] : [null, null];
                if (p == null) continue;
                wanted.set(q, Math.min(wanted.has(q) ? wanted.get(q) : Infinity, rowOf.get(p)));
            }
            /* No rung, no ladder: with nothing to align, every partner row sorted below the
               primary's and the two columns ran off in opposite directions. The ring holds. */
            if (!wanted.size) return;
            const byOrder = (x, y) => parentOrder.indexOf(x.parentNodeId) - parentOrder.indexOf(y.parentNodeId)
                || x.relationIndex - y.relationIndex;
            const partnersOf = new Map();
            const unjoinedPartners = [];
            for (const n of partnerSubs.slice().sort(byOrder)) {
                n.ladderSide = "partner";
                if (!wanted.has(n.id)) { unjoinedPartners.push(n); continue; }
                const i = wanted.get(n.id);
                if (!partnersOf.has(i)) partnersOf.set(i, []);
                partnersOf.get(i).push(n);
            }

            let row = 0;
            const unjoinedPrimary = [];
            primarySubs.forEach((n, i) => {
                n.ladderSide = "primary";
                const mine = partnersOf.get(i) || [];
                if (!mine.length && n.relationUnconnected) { unjoinedPrimary.push(n); return; }
                n.ladderRow = row;
                mine.forEach((q, k) => { q.ladderRow = row + k; });
                row += Math.max(1, mine.length);
            });
            unjoinedPrimary.forEach((n, k) => { n.ladderRow = row + k; });
            unjoinedPartners.forEach((n, k) => { n.ladderRow = row + k; });
            const rows = row + Math.max(unjoinedPrimary.length, unjoinedPartners.length);
            for (const n of primarySubs.concat(partnerSubs)) n.ladderRows = Math.max(1, rows);
        },

        /**
         * Where one ladder slice sits. The columns face each other between the primary and its
         * partners, each inset from its own parent; if the parents are too close for two labels
         * along one line, the columns spread around the midpoint instead.
         */
        _ladderPosition(d, byId, geom) {
            const p = byId.get(this.primaryExpandedNodeId);
            const partners = this.expandedNodeIds
                .filter(id => id !== this.primaryExpandedNodeId)
                .map(id => byId.get(id))
                .filter(n => n && Number.isFinite(n.x));
            if (!p || !Number.isFinite(p.x) || !partners.length) return null;
            const qx = partners.reduce((s, n) => s + n.x, 0) / partners.length;
            const qy = partners.reduce((s, n) => s + n.y, 0) / partners.length;
            const dir = Math.sign(qx - p.x) || (this._clusterOf(p) === 2 ? -1 : 1);

            let colP = p.x + dir * LADDER_INSET;
            let colQ = qx - dir * LADDER_INSET;
            if ((colQ - colP) * dir < LADDER_MIN_GAP) {
                const mid = (p.x + qx) / 2;
                colP = mid - dir * (LADDER_MIN_GAP / 2);
                colQ = mid + dir * (LADDER_MIN_GAP / 2);
            }
            const cy = Number.isFinite(p.y) ? (p.y + qy) / 2 : geom.cyMid;
            const rows = Math.max(1, Number(d.ladderRows) || 1);
            return {
                x: d.ladderSide === "primary" ? colP : colQ,
                y: cy + ((Number(d.ladderRow) || 0) - (rows - 1) / 2) * LADDER_ROW,
            };
        },

        /** The way a ladder column faces — towards the other lane (cluster 1 sits on the left). */
        _ladderInward(d) {
            return this._clusterOf(d) === 2 ? -1 : 1;
        },

        /**
         * The ladder drawn as a patch bay: one layer holding everything between the two columns.
         *
         *  - Cables, not dotted arrows. Each leaves its id horizontally and eases into the other,
         *    so a level pair is a straight rung and a one-to-many fans as S-curves that never meet
         *    at an endpoint. The join is mutual, so no arrowheads.
         *  - A mid-cable mark says what the join is: filled where the same id sits in both clusters
         *    (the ids overlap), hollow on a dashed cable where two different ids are linked.
         *  - A bus bracket per column replaces the fan of tethers — one trunk from the parent to a
         *    spine, a short tick to each id — so nothing diagonal crosses the rows.
         *  - Zebra bands on alternate joined rows carry the eye across; a caption names the bridge.
         *  - Hovering a cable or either of its ids lights that pair and quiets the rest.
         *
         * @returns {{update: Function, hot: Function}|null} `update` re-reads positions each tick.
         */
        _buildLadderBridge(layer, rungs, subs, isDark, root) {
            if (!subs.length) return null;
            const rose = this.clusterEdgeWeight.color;
            const nr = d => this.nodeRadius(d);
            const inward = d => this._ladderInward(d);
            const idOf = v => (typeof v === "object" && v !== null ? v.id : v);
            const byId = new Map(this.graphNodes.map(n => [n.id, n]));
            const sameId = e => String(e.source.entityId) === String(e.target.entityId);
            const rowOf = e => Math.min(Number(e.source.ladderRow) || 0, Number(e.target.ladderRow) || 0);
            /* Left end first, so a cable's geometry does not depend on the side the payload named. */
            const ends = e => ((e.source.x || 0) <= (e.target.x || 0) ? [e.source, e.target] : [e.target, e.source]);

            const joinedRows = rungs.reduce((m, e) => Math.max(m,
                Number(e.source.ladderRow) + 1, Number(e.target.ladderRow) + 1), 0);
            const bandRows = [];
            for (let r = 0; r < joinedRows; r += 2) bandRows.push(r);

            const bands = layer.append("g").attr("class", "cgl-bands")
                .selectAll("rect").data(bandRows).enter().append("rect")
                .attr("class", "cgl-band")
                .attr("rx", 9)
                .attr("height", LADDER_ROW - 8)
                .attr("fill", rose)
                .attr("fill-opacity", isDark ? 0.07 : 0.045);

            /* ── Caption ──
               Counts what is joined, not what fits: past the row budget the canvas draws the
               first rows only, and a caption reading "24 SHARED" over a hundred shared ids would
               understate the recommendation it is there to summarise. */
            const primary = byId.get(this.primaryExpandedNodeId);
            const partnerNodes = this.expandedNodeIds
                .filter(id => id !== this.primaryExpandedNodeId)
                .map(id => byId.get(id)).filter(Boolean);
            const partnerIdSets = partnerNodes.map(q => ({
                label: String(q.neighborType || ""),
                ids: new Set((q.entityIds || []).map(String)),
            }));
            const totalPairs = new Set();
            let totalShared = 0;
            for (const d of (primary && primary.clusterEdgeDetails) || []) {
                const holders = partnerIdSets.filter(q => q.label === String(d.pairLabel));
                for (const to of d.ids || []) {
                    if (!holders.some(q => q.ids.has(String(to)))) continue;
                    const pk = `${d.fromId}|${to}`;
                    if (totalPairs.has(pk)) continue;
                    totalPairs.add(pk);
                    if (String(d.fromId) === String(to)) totalShared += 1;
                }
            }
            const drawnShared = rungs.filter(sameId).length;
            const drawnLinked = rungs.length - drawnShared;
            const shared = Math.max(drawnShared, totalShared);
            const linked = Math.max(drawnLinked, totalPairs.size - totalShared);
            const hiddenPairs = Math.max(0, shared + linked - rungs.length);
            const part = (drawn, total, word) => (!total ? ""
                : drawn < total ? `${drawn} OF ${total} ${word}` : `${total} ${word}`);
            const partnerTypes = [...new Set(subs.filter(s => s.ladderSide === "partner")
                .map(s => String((byId.get(s.parentNodeId) || {}).neighborType || "")).filter(Boolean))];
            const captionText = [
                part(drawnShared, shared, "SHARED"),
                part(drawnLinked, linked, "LINKED"),
            ].filter(Boolean).join(" · ") || "NO JOINS";
            const pairText = `${(primary && primary.neighborType) || ""}  ⇄  ${partnerTypes.join(", ")}`;
            const caption = layer.append("g").attr("class", "cgl-caption").attr("pointer-events", "none");
            const capRuleL = caption.append("line").attr("stroke", rose).attr("stroke-opacity", 0.35).attr("stroke-width", 1);
            const capRuleR = caption.append("line").attr("stroke", rose).attr("stroke-opacity", 0.35).attr("stroke-width", 1);
            const capMain = caption.append("text")
                .attr("text-anchor", "middle")
                .attr("font-size", "10px")
                .attr("font-weight", "800")
                .attr("letter-spacing", "0.16em")
                .attr("fill", rose)
                .text(captionText);
            const capSub = caption.append("text")
                .attr("text-anchor", "middle")
                .attr("font-size", "9.5px")
                .attr("font-family", "ui-monospace,monospace")
                .attr("fill", isDark ? "#94a3b8" : "#64748b")
                .text(pairText);

            /* ── Show more ──
               Under the ladder when joins are left off it: one click adds a step of rows to both
               columns. At the cap it stops offering and points at the panel, which lists all. */
            const canMore = hiddenPairs > 0 && this.ladderCap < LADDER_CAP_MAX;
            const more = layer.append("g").attr("class", "cgl-more")
                .style("display", hiddenPairs > 0 ? null : "none")
                .style("cursor", canMore ? "pointer" : "default");
            const moreBg = more.append("rect")
                .attr("rx", 11).attr("height", 22).attr("y", -11)
                .attr("fill", isDark ? "#1f0a14" : "#fff1f5")
                .attr("stroke", rose).attr("stroke-opacity", canMore ? 0.9 : 0.35)
                .attr("stroke-dasharray", canMore ? null : "3 3");
            const moreText = more.append("text")
                .attr("text-anchor", "middle").attr("dominant-baseline", "central")
                .attr("font-size", "10px").attr("font-weight", "700").attr("letter-spacing", "0.04em")
                .attr("fill", rose)
                .text(canMore
                    ? `+ Show ${Math.min(LADDER_CAP_STEP, hiddenPairs)} more  ·  ${hiddenPairs} not drawn`
                    : `${hiddenPairs} more joins — listed in the side panel`);
            const moreW = moreText.text().length * 5.9 + 26;
            moreBg.attr("x", -moreW / 2).attr("width", moreW);
            if (canMore) {
                more.on("click", ev => { ev.stopPropagation(); this.showMoreLadderRows(); })
                    .on("mouseenter", () => moreBg.attr("fill", isDark ? "#3b0a1e" : "#ffe4ec"))
                    .on("mouseleave", () => moreBg.attr("fill", isDark ? "#1f0a14" : "#fff1f5"));
            }

            /* ── Bus brackets ── */
            const columns = [...new Set(subs.map(s => s.parentNodeId))].map(pid => ({
                parent: byId.get(pid),
                subs: subs.filter(s => s.parentNodeId === pid),
            })).filter(c => c.parent);
            /* Several partners share one column; each bracket gets its own spine, stepped
               outward, so which ticks belong to which group stays readable. */
            let partnerSpine = 0;
            for (const c of columns) {
                c.spineStep = c.subs[0] && c.subs[0].ladderSide === "partner" ? partnerSpine++ : 0;
            }
            const tethers = layer.append("g").attr("class", "cgl-tethers")
                .selectAll("path").data(columns).enter().append("path")
                .attr("fill", "none")
                .attr("stroke", c => this.linkStroke({ linkKind: c.parent.nodeLinkKind || "c1" }))
                .attr("stroke-width", 1.3)
                .attr("stroke-opacity", 0.5)
                .attr("stroke-linecap", "round")
                .attr("stroke-linejoin", "round");

            /* ── Cables ── */
            const rung = layer.append("g").attr("class", "cgl-rungs")
                .selectAll("g").data(rungs).enter().append("g")
                .attr("class", "cgl-rung");
            const cable = rung.append("path")
                .attr("class", "cgl-cable")
                .attr("fill", "none")
                .attr("stroke", rose)
                .attr("stroke-width", 1.7)
                .attr("stroke-opacity", 0.8)
                .attr("stroke-linecap", "round")
                .attr("stroke-dasharray", e => (sameId(e) ? null : "5 4"));
            const mark = rung.append("circle")
                .attr("class", "cgl-mark")
                .attr("r", 3.4)
                .attr("fill", e => (sameId(e) ? rose : (isDark ? "#0b1220" : "#ffffff")))
                .attr("stroke", rose)
                .attr("stroke-width", 1.4);
            /* Wide and invisible: a 1.7px cable is too thin to hover reliably. */
            const hit = rung.append("path")
                .attr("fill", "none")
                .attr("stroke", "transparent")
                .attr("stroke-width", 12)
                .style("cursor", "default");
            rung.append("title").text(e => (sameId(e)
                ? `Same id in both clusters\n${e.source.entityId}`
                : `Linked ids\n${ends(e)[0].entityId}\n⇄ ${ends(e)[1].entityId}`));

            /* ── Hover focus ── */
            const hot = focusOn => {
                const touches = e => focusOn && (focusOn.__rung === e
                    || idOf(e.source) === focusOn.id || idOf(e.target) === focusOn.id);
                layer.classed("has-hot", !!focusOn);
                rung.classed("is-hot", e => !!touches(e));
                const hotRows = new Set(rungs.filter(touches).map(rowOf));
                bands.classed("is-hot", r => hotRows.has(r));
                const hotIds = new Set();
                rungs.filter(touches).forEach(e => { hotIds.add(idOf(e.source)); hotIds.add(idOf(e.target)); });
                root.selectAll(".cgn").classed("cgn-ladder-hot", n => hotIds.has(n.id))
                    .classed("cgn-ladder-cold", n => !!focusOn && !!n.ladderSide && !hotIds.has(n.id));
            };
            hit.on("mouseenter", (ev, e) => hot({ __rung: e }))
                .on("mouseleave", () => hot(null));

            /* ── Reveal: cables draw in row by row. Solid ones trace from end to end; dashed ones,
               whose dash pattern the trace would overwrite, fade in. ── */
            const reduce = typeof window !== "undefined" && window.matchMedia
                && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            const fresh = this._newSubNodeIds || new Set();
            const isFresh = e => fresh.has(idOf(e.source)) || fresh.has(idOf(e.target));
            if (!reduce) {
                cable.filter(e => isFresh(e) && sameId(e))
                    .attr("pathLength", 1)
                    .attr("stroke-dasharray", "1 1")
                    .attr("stroke-dashoffset", 1)
                    .transition().delay(e => 120 + 38 * rowOf(e)).duration(420).ease(d3.easeCubicOut)
                    .attr("stroke-dashoffset", 0)
                    .on("end", function () {
                        d3.select(this).attr("pathLength", null).attr("stroke-dasharray", null).attr("stroke-dashoffset", null);
                    });
                cable.filter(e => isFresh(e) && !sameId(e))
                    .attr("stroke-opacity", 0)
                    .transition().delay(e => 120 + 38 * rowOf(e)).duration(360)
                    .attr("stroke-opacity", 0.8);
                mark.filter(isFresh)
                    .attr("r", 0)
                    .transition().delay(e => 320 + 38 * rowOf(e)).duration(240).ease(d3.easeBackOut)
                    .attr("r", 3.4);
                caption.attr("opacity", 0).transition().delay(80).duration(300).attr("opacity", 1);
            }

            const cablePath = e => {
                const [a, b] = ends(e);
                const x1 = a.x + nr(a) + 3;
                const x2 = b.x - nr(b) - 3;
                const k = Math.max(0, (x2 - x1) * 0.46);
                return `M${x1},${a.y}C${x1 + k},${a.y} ${x2 - k},${b.y} ${x2},${b.y}`;
            };

            const update = () => {
                const ok = n => n && Number.isFinite(n.x) && Number.isFinite(n.y);
                if (!subs.every(ok)) return;
                const xs = subs.map(s => s.x);
                const xMin = Math.min(...xs);
                const xMax = Math.max(...xs);
                const yOfRow = new Map(subs.map(s => [Number(s.ladderRow) || 0, s.y]));
                const yMin = Math.min(...subs.map(s => s.y));
                const yMax = Math.max(...subs.map(s => s.y));

                bands
                    .attr("x", xMin - 16)
                    .attr("width", Math.max(0, xMax - xMin + 32))
                    .attr("y", r => (yOfRow.has(r) ? yOfRow.get(r) : yMin) - (LADDER_ROW - 8) / 2);

                const mid = (xMin + xMax) / 2;
                const capY = yMin - LADDER_ROW * 0.95;
                capMain.attr("x", mid).attr("y", capY);
                capSub.attr("x", mid).attr("y", capY + 13);
                const half = Math.max(40, (xMax - xMin) / 2 - 20);
                const ruleGap = captionText.length * 3.9 + 14;
                capRuleL.attr("x1", mid - half).attr("x2", mid - ruleGap).attr("y1", capY - 3.5).attr("y2", capY - 3.5);
                capRuleR.attr("x1", mid + ruleGap).attr("x2", mid + half).attr("y1", capY - 3.5).attr("y2", capY - 3.5);
                /* Clear of the bottom row's merge pill. */
                more.attr("transform", `translate(${mid},${yMax + LADDER_ROW * 1.05})`);

                tethers.attr("d", c => {
                    const p = c.parent;
                    const live = c.subs.filter(ok);
                    if (!ok(p) || !live.length) return null;
                    const dir = inward(live[0]);
                    const spineX = live[0].x - dir * (nr(live[0]) + 16 + c.spineStep * 7);
                    const ys = live.map(s => s.y);
                    const top = Math.min(...ys);
                    const bottom = Math.max(...ys);
                    const trunkY = Math.max(top, Math.min(bottom, p.y));
                    const startX = p.x + dir * (nr(p) + 4);
                    let d = `M${startX},${p.y}L${spineX},${trunkY}M${spineX},${top}V${bottom}`;
                    for (const s of live) d += `M${spineX},${s.y}H${s.x - dir * (nr(s) + 2)}`;
                    return d;
                });

                const live = e => ok(e.source) && ok(e.target);
                cable.attr("d", e => (live(e) ? cablePath(e) : null));
                hit.attr("d", e => (live(e) ? cablePath(e) : null));
                /* The cubic's midpoint, where both tangents balance — on the curve, not the chord. */
                mark.attr("cx", e => (live(e) ? (ends(e)[0].x + ends(e)[1].x) / 2 : -9999))
                    .attr("cy", e => (live(e) ? (ends(e)[0].y + ends(e)[1].y) / 2 : -9999));
            };
            update();
            return { update, hot };
        },

        /** The parent's cluster-edge detail narrowed to one sub-node's ids. */
        _clusterEdgeSliceFor(parent, ids) {
            if (!parent.hasClusterEdge) return {};
            const keep = new Set(ids.map(String));
            const details = (parent.clusterEdgeDetails || []).filter(d => keep.has(String(d.fromId)));
            return {
                hasClusterEdge: details.length > 0,
                clusterEdgeDetails: details,
                clusterEdgeEntityIds: [...new Set(details.map(d => String(d.fromId)))],
                clusterEdgeCount: details.reduce((sum, d) => sum + d.ids.length, 0),
            };
        },

        /* ── Graph lifecycle ──────────────────────────────────────────── */
        loadGraph(rawInput) {
            this._clearInspTimer();
            this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };

            /* Kept unexpanded so toggling expansion re-derives from the same source. */
            this._baseData = rawInput;
            /* Drop expansions whose parent is gone (new recommendation, filter, cluster move). */
            const liveIds = new Set(rawInput.nodes.map(n => n.id));
            const stillLive = this.expandedNodeIds.filter(id => liveIds.has(id));
            if (stillLive.length !== this.expandedNodeIds.length) this.expandedNodeIds = stillLive;
            if (this.primaryExpandedNodeId && !liveIds.has(this.primaryExpandedNodeId)) {
                this.expandedNodeIds = [];
                this.primaryExpandedNodeId = null;
            } else if (this.primaryExpandedNodeId) {
                /* Partners are re-read from the new data, not kept from the click: a move changes
                   which nodes the open one joins — a moved half is a new node, a fully merged
                   partner joins nothing — and a stale partner stayed open with no line to it. */
                const k = this.primaryExpandedNodeId;
                this.expandedNodeIds = [k, ...this._crossClusterPartnerIds(k)];
            }

            const inputData = this._visibleGraph(rawInput);
            this._focusIds = inputData.focusIds || null;

            // Save current positions + linkKind so we can detect membership changes on re-render
            if (!this._posCache) this._posCache = new Map();
            /* Slices with no cached position have just appeared; only those get the reveal, so
               merging one does not replay the animation for every sibling. */
            this._newSubNodeIds = new Set(
                inputData.nodes.filter(n => n.isRelationSubNode && !this._posCache.has(n.id)).map(n => n.id)
            );
            for (const n of this.graphNodes) {
                if (n.x != null && n.y != null) {
                    this._posCache.set(n.id, {
                        x: n.x, y: n.y,
                        nodeLinkKind: n.nodeLinkKind ?? null,
                        /* A hand-placed node has to survive the redraw that any expansion or merge
                           triggers, or the hold lasts only until the next click. */
                        fx: n.fx ?? null, fy: n.fy ?? null,
                        userPlaced: !!n.userPlaced, pinned: !!n.pinned,
                    });
                }
            }

            // Detect which nodes changed cluster membership (override added OR removed).
            // We still restore their positions so there's no jump — the simulation will
            // pull them smoothly to the correct lane with a higher alpha target.
            let membershipChangedCount = 0;
            this.graphNodes = inputData.nodes.map(n => {
                const prev = this._posCache.get(n.id);
                if (!prev) return { ...n };
                const changedCluster =
                    n.type !== 'cluster' && prev.nodeLinkKind !== (n.nodeLinkKind ?? null);
                if (changedCluster) membershipChangedCount++;
                // Always restore position (visual continuity, no jump to origin)
                const out = { ...n, x: prev.x, y: prev.y };
                /* Carry a hand-placed or pinned hold across the redraw — except when the node has
                   changed cluster, where its new lane decides the position and holding the old spot
                   would leave it sitting on the wrong side. */
                if (!changedCluster && (prev.userPlaced || prev.pinned)) {
                    out.userPlaced = prev.userPlaced;
                    out.pinned = prev.pinned;
                    out.fx = prev.fx;
                    out.fy = prev.fy;
                }
                return out;
            });
            this._membershipChangedCount = membershipChangedCount;

            this.graphEdges = inputData.edges.map(e => {
                const src = typeof e.source === "object" ? e.source.id : e.source;
                const tgt = typeof e.target === "object" ? e.target.id : e.target;
                const out = { source: src, target: tgt };
                if (e.linkKind) out.linkKind = e.linkKind;
                return out;
            });

            /* Colour map keyed by the FULL neighborType value (e.g. "prsn~aqfer.com"), so every
               type~domain gets its own colour rather than every prefix — two `ck~…` domains are
               different groups and have to look it.
               
               Every key is collected before any colour is handed out: the palette spreads over the
               number of types this graph actually has, which it cannot do while assigning
               incrementally. Slices are skipped — they carry their parent's key and would not add
               one anyway. */
            const paletteKeys = new Set();
            this.graphNodes.forEach(n => {
                if (n.type === "cluster" || n.isRelationSubNode) return;
                paletteKeys.add(n.neighborType
                    ? String(n.neighborType).trim().toLowerCase()
                    : n.type);
            });
            const sortedKeys = [...paletteKeys].filter(Boolean).sort();
            const colorMap = new Map();
            sortedKeys.forEach((key, i) => colorMap.set(key, categoricalColor(i, sortedKeys.length)));
            this.prefixColorMap = colorMap;

            this.hasGraph = true;
            this.updateStats();
            this.$nextTick(() => this.drawGraph());
        },

        drawGraph() {
            const container = this.$refs.graphContainer;
            if (!container) return;

            /* The previous simulation has to be shut down before another is built.
 
               Its `arrange` and `fan` forces read `vm.graphNodes` — the live array, not the one it
               was constructed with — so a leaked simulation keeps adding velocity to the nodes of
               the new one. Nothing damps that contribution, because d3 integrates only its own
               node array, and every redraw leaked one more: after a few moves several simulations
               were pushing the same nodes at once, positions diverged, and `translate()` was
               eventually handed a number that was not one.
 
               The tick handler is detached too, not just paused: it writes transforms into the SVG
               this call is about to replace, and an old drag closure can still call
               `alphaTarget().restart()` on a merely-stopped simulation. */
            if (this.simulation) {
                this.simulation.on("tick", null);
                this.simulation.on("end", null);
                this.simulation.stop();
                this.simulation = null;
            }

            /* `viewBox="0 0 0 H"` is degenerate and renders nothing at all, so an unmeasured
               container gets a working width instead. `_drewAtFallbackWidth` remembers that the
               layout was computed for a guess, so the resize observer redraws rather than merely
               re-framing when the real width arrives. */
            const W = container.clientWidth || 900;
            const H = container.clientHeight || 600;
            this._drewAtFallbackWidth = !container.clientWidth;
            const isLarge = this.graphNodes.length > 200 || this.graphEdges.length > 250;
            const isDark = this.isDark;
            const vm = this;
            const nr = d => this.nodeRadius(d);

            d3.select(container).select("svg").remove();

            const svg = d3.select(container)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${W} ${H}`);

            /* ── SVG defs: shadow filter + arrowhead ── */
            const defs = svg.append("defs");
            const sid = `${this.graphUid}-shadow`;
            const f = defs.append("filter")
                .attr("id", sid)
                .attr("x", "-50%").attr("y", "-50%")
                .attr("width", "200%").attr("height", "200%");
            f.append("feDropShadow")
                .attr("dx", 0).attr("dy", 2.5)
                .attr("stdDeviation", isDark ? 5 : 4)
                .attr("flood-color", isDark ? "rgba(0,0,0,0.6)" : "rgba(15,23,42,0.22)")
                .attr("flood-opacity", 1);

            /* Arrowhead for bidirectional edges. `auto-start-reverse` lets one marker serve both
               ends — as marker-start it flips, so both heads point away from the line. Redrawn per
               graph load because the fill is a fixed colour, not context-stroke: Safari still does
               not support that keyword and would render the heads black. */
            const arrowId = `${this.graphUid}-arrow`;
            defs.append("marker")
                .attr("id", arrowId)
                .attr("viewBox", "0 -5 10 10")
                .attr("refX", 9).attr("refY", 0)
                .attr("markerWidth", 4.5).attr("markerHeight", 4.5)
                .attr("orient", "auto-start-reverse")
                .append("path")
                .attr("d", "M0,-4L9,0L0,4")
                .attr("fill", this.clusterEdgeWeight.color);

            const g = svg.append("g");

            this.zoomBehavior = d3.zoom()
                .scaleExtent([0.04, 2.2])
                .on("zoom", event => g.attr("transform", event.transform));
            svg.call(this.zoomBehavior);

            svg.on("click", () => {
                this._clearInspTimer();
                this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
                this._clearHighlight(g);
                this.detailNode = null;
                this.collapseAllNodes();
            });

            this.svg = svg;

            /* ── Simulation ── */
            let   isDragging = false;

            const clusterOf = d => this._clusterOf(d);

            /* Layout targets are derived, not emergent — see _layoutTargets. */
            const geom = this._layoutTargets(this.graphNodes, W, H, this.graphEdges);
            const { cx, cyMid, laneX } = geom;

            /* Seed: unpositioned nodes start at their slot, so the first paint is already tidy.
               Slices start at their parent, which is what the reveal transition reads as. */
            /* Both halves, and finite: the guard used to test `n.x != null` alone, so a node with
               a good x and a NaN y was treated as positioned and kept it — and NaN is not null. */
            const placed = n => Number.isFinite(n.x) && Number.isFinite(n.y);
            const seedById = new Map(this.graphNodes.map(n => [n.id, n]));
            this.graphNodes.forEach(n => {
                if (placed(n) || !n.isRelationSubNode) return;
                const p = seedById.get(n.parentNodeId);
                if (p && placed(p)) { n.x = p.x; n.y = p.y; }
                else if (p && Number.isFinite(p.tx) && Number.isFinite(p.ty)) { n.x = p.tx; n.y = p.ty; }
            });
            this.graphNodes.forEach(n => {
                if (placed(n)) return;
                n.x = Number.isFinite(n.tx) ? n.tx : cx;
                n.y = Number.isFinite(n.ty) ? n.ty : cyMid;
            });

            /* Annotation edges are visual-only: resolve their node refs so the tick can read .x/.y.
               Nothing exerts link attraction now that positions are derived. */
            const nodeById = new Map(this.graphNodes.map(n => [n.id, n]));
            for (const e of this.graphEdges) {
                e.source = nodeById.get(typeof e.source === "object" ? e.source.id : e.source) || e.source;
                e.target = nodeById.get(typeof e.target === "object" ? e.target.id : e.target) || e.target;
            }

            this.simulation = d3.forceSimulation(this.graphNodes)
                /* Gentle only: it resolves residual overlap without dragging nodes off their slot. */
                .force("collision", d3.forceCollide().radius(d => nr(d) + 8).iterations(1))
                /* Relax toward the computed slot. A dragged node has fx/fy set, which d3 applies
                   after forces, so this cannot fight the drag. */
                .force("arrange", () => {
                    for (const n of vm.graphNodes) {
                        if (n.isRelationSubNode || n.tx == null || n.x == null) continue;
                        /* Dropped by hand, or pinned by double-click: the layout yielded to the
                           user and must not drag it back to its slot. */
                        if (n.userPlaced || n.pinned) continue;
                        /* Firm: pinned slices push through the collision force, and a child that
                           yields to them leaves its arc slot and the ring stops reading as a ring. */
                        const k = n.type === "cluster" ? 0.6 : 0.45;
                        n.vx += (n.tx - n.x) * k;
                        n.vy += (n.ty - n.y) * k;
                    }
                })
                /* Slices: seated on the reserved ring outside every child ring, at their parent's
                   own angle.

                   They used to sit in the band between hub and arc, which is only empty when
                   nothing else is there — with children on an inner ring the slices landed on top
                   of them, and a slice covering a node takes the click meant for it. A radius no
                   child occupies removes the possibility rather than reducing it.

                   Assigned rather than nudged: a force lost the row's alignment to collision, and
                   these are an annotation of one node, not free bodies. */
                .force("fan", () => {
                    const subs = vm.graphNodes.filter(d => d.isRelationSubNode);
                    if (!subs.length) return;
                    const byId = new Map(vm.graphNodes.map(d => [d.id, d]));
                    for (const d of subs) {
                        /* Yield for the duration of a drag: the fan assigns fx/fy every tick, so
                           holding it here is what made an exploded id impossible to drag. */
                        if (d.dragging) continue;
                        const p = byId.get(d.parentNodeId);
                        if (!p || p.x == null) continue;
                        const pos = (d.ladderSide && vm._ladderPosition(d, byId, geom))
                            || vm._slicePosition(p, vm._clusterOf(p), d, geom);
                        d.x = d.fx = pos.x;
                        d.y = d.fy = pos.y;
                    }
                })
                .velocityDecay(0.42)
                .alphaDecay(0.045);

            // Tune simulation energy based on what changed:
            const totalNodes = this.graphNodes.length;
            const positionedCount = this.graphNodes.filter(n => Number.isFinite(n.x)).length;
            const allPositioned = totalNodes > 0 && positionedCount === totalNodes;
            const changed = this._membershipChangedCount || 0;

            if (allPositioned && changed > 0) {
                // Nodes changed cluster (override added/removed): pull them to correct lane
                this.simulation.alpha(0.65).alphaDecay(0.035);
            } else if (allPositioned) {
                // Pure cosmetic re-render: settle instantly
                this.simulation.alpha(0.12).alphaDecay(0.08);
            } else if (positionedCount > 0 && positionedCount / totalNodes >= 0.7) {
                // Mostly positioned — a few new nodes (e.g. moved-IDs split node appeared):
                // moderate energy so new nodes settle near their target cluster quickly
                this.simulation.alpha(0.5).alphaDecay(0.04);
            }
            // else: first load or many new nodes → default alpha(1)

            this._membershipChangedCount = 0;  // reset so it doesn't affect drag-restart calls

            /* ── Edges ── */
            /* SVG paints in document order, so the layer a curator acts on is sorted last:
               membership, then cluster edges, then the amber connecting lines on top. Sorting here
               rather than relying on the order the graph builder happened to push them in. */
            const paintRank = e =>
                e.linkKind === EXPAND_LINK_KIND ? 1
                    : e.linkKind === CLUSTER_EDGE_LINK_KIND ? 2
                    : ANNOTATION_LINK_KINDS.has(e.linkKind) ? 3 : 0;
            /* Ladder rungs and tethers are drawn by the bridge layer below instead: as cables and a
               bus bracket they need a shape a straight <line> cannot take. They stay in graphEdges,
               so counts, focus and highlighting read them as before. */
            const inLadder = v => typeof v === "object" && v !== null && !!v.ladderSide;
            const isRung = e => ANNOTATION_LINK_KINDS.has(e.linkKind) && inLadder(e.source) && inLadder(e.target);
            const isTether = e => e.linkKind === EXPAND_LINK_KIND && inLadder(e.target);
            const link = g.selectAll(".cge")
                .data(this.graphEdges
                    .filter(e => !isRung(e) && !isTether(e))
                    .sort((a, b) => paintRank(a) - paintRank(b))).enter()
                .append("line")
                .attr("class", "cge")
                .attr("stroke",           d => this.linkStroke(d))
                .attr("stroke-width",     d => this.linkWidth(d))
                .attr("stroke-dasharray", d => this.linkDash(d) || null)
                .attr("stroke-linecap",   d => this.linkCap(d))
                .attr("stroke-opacity",   d => this.linkOpacity(d))
                /* Both ends: the join these lines carry is mutual, not directed from the side the
                   payload happens to state it from. */
                .attr("marker-start",     d => d.bidirectional ? `url(#${arrowId})` : null)
                .attr("marker-end",       d => d.bidirectional ? `url(#${arrowId})` : null);

            /* Above the edges, under the nodes: the halo on each id label masks the cable beneath. */
            this._ladderBridge = this._buildLadderBridge(
                g.append("g").attr("class", "cgl-bridge"),
                this.graphEdges.filter(isRung),
                this.graphNodes.filter(n => n.ladderSide),
                isDark,
                g
            );

            /* ── Helper: find current cluster hub id for a node via edges ── */
            const getId = v => (typeof v === "object" && v !== null) ? v.id : v;
            const findNodeCluster = (nodeId) => {
                for (const e of vm.graphEdges) {
                    if (ANNOTATION_LINK_KINDS.has(e.linkKind)) continue;
                    if (e.linkKind === EXPAND_LINK_KIND) continue;
                    const src = getId(e.source);
                    const tgt = getId(e.target);
                    if (src === nodeId) {
                        const tn = vm.graphNodes.find(n => n.id === tgt);
                        if (tn && tn.type === "cluster") return tgt;
                    } else if (tgt === nodeId) {
                        const sn = vm.graphNodes.find(n => n.id === src);
                        if (sn && sn.type === "cluster") return src;
                    }
                }
                return null;
            };

            /* ── Node groups ── */
            let dragTarget = null;  // cluster node currently in drop zone during drag

            const node = g.selectAll(".cgn")
                .data(this.graphNodes).enter()
                .append("g")
                .attr("class", "cgn")
                .call(
                    d3.drag()
                        /* ── Drag start ── */
                        .on("start", (ev, d) => {
                            // Always stop SVG-level zoom/pan from hijacking the drag
                            if (ev.sourceEvent) ev.sourceEvent.stopPropagation();
                            /* An exploded id drags like any other node — it is the unit a move
                               works in. The fan force is told to let go for the duration, or it
                               would reassign fx/fy on the very next tick. */
                            if (d.isRelationSubNode) {
                                if (!vm.enableNodeMove) return;
                                d.dragging = true;
                                d.fx = d.x; d.fy = d.y;
                                return;
                            }

                            if (vm.enableNodeMove && d.type !== "cluster") {
                                // Node-move mode: pin node in place, do NOT reheat simulation
                                d.fx = d.x; d.fy = d.y;
                                return;
                            }

                            // Normal layout drag: reheat simulation
                            isDragging = true;
                            if (!ev.active) vm.simulation.alphaTarget(0.1).restart();
                            d.fx = d.x; d.fy = d.y;
                        })

                        /* ── Drag move ── */
                        .on("drag", (ev, d) => {
                            d.fx = ev.x; d.fy = ev.y;

                            if (!vm.enableNodeMove || d.type === "cluster") return;

                            // Detect which cluster hub the node is hovering over
                            const hubs = vm.graphNodes.filter(n => n.type === "cluster");
                            let nearest = null;
                            let nearestDist = Infinity;
                            for (const hub of hubs) {
                                if (hub.x == null || hub.y == null) continue;
                                const dx = ev.x - hub.x;
                                const dy = ev.y - hub.y;
                                const dist = Math.sqrt(dx * dx + dy * dy);
                                // Drop zone: node radius + generous padding so it's easy to hit
                                if (dist < nr(hub) + 90 && dist < nearestDist) {
                                    nearestDist = dist;
                                    nearest = hub;
                                }
                            }

                            if (nearest !== dragTarget) {
                                // Reset previous target highlight
                                if (dragTarget) {
                                    g.selectAll(".cgn").filter(n => n === dragTarget)
                                        .select(".cgn-circle")
                                        .attr("stroke", () => vm.nodeCircleStroke(dragTarget).color)
                                        .attr("stroke-width", () => vm.nodeCircleStroke(dragTarget).width);
                                }
                                if (nearest) {
                                    // Highlight new drop target with green ring
                                    g.selectAll(".cgn").filter(n => n === nearest)
                                        .select(".cgn-circle")
                                        .attr("stroke", "#10b981")
                                        .attr("stroke-width", 7);
                                }
                                dragTarget = nearest;
                            }
                        })

                        /* ── Drag end ── */
                        .on("end", (ev, d) => {
                            // Always reset cluster highlight
                            if (dragTarget) {
                                g.selectAll(".cgn").filter(n => n === dragTarget)
                                    .select(".cgn-circle")
                                    .attr("stroke", () => vm.nodeCircleStroke(dragTarget).color)
                                    .attr("stroke-width", () => vm.nodeCircleStroke(dragTarget).width);
                            }

                            /* An exploded id is not a member of a cluster in its own right, so a
                               drop is a merge of that one id rather than a node move — the same
                               staging the merge pill uses, keyed by (parent node, entity id). */
                            if (d.isRelationSubNode) {
                                d.dragging = false;
                                const parent = vm.graphNodes.find(n => n.id === d.parentNodeId);
                                const ownSide = vm._clusterOf(parent || d);
                                if (dragTarget && d.entityId != null &&
                                    vm._clusterOf(dragTarget) !== ownSide && !d.relationAllMerged) {
                                    vm.emitMergeConnection(d, {
                                        ids: [d.entityId],
                                        targets: d.relationTargets || [],
                                    });
                                }
                                dragTarget = null;
                                /* Back to the fan: its seat is the parent's business, and after a
                                   merge the redraw decides where it belongs anyway. */
                                d.fx = null; d.fy = null;
                                if (!ev.active) {
                                    vm.simulation.alphaTarget(0.04).restart();
                                    setTimeout(() => vm.simulation.alphaTarget(0), 400);
                                }
                                return;
                            }

                            if (vm.enableNodeMove && d.type !== "cluster") {
                                // Emit move if dropped on a different cluster
                                let moved = false;
                                if (dragTarget) {
                                    const fromClusterId = findNodeCluster(d.id);
                                    const toClusterId = dragTarget.id;
                                    if (fromClusterId && fromClusterId !== toClusterId) {
                                        moved = true;
                                        vm.$emit("node-moved", {
                                            nodeId: d.id,
                                            node: { ...d },
                                            fromClusterId,
                                            toClusterId,
                                        });
                                    }
                                }
                                dragTarget = null;
                                /* Dropped somewhere the user chose, so it stays there. Releasing
                                   fx/fy here handed the node straight back to the layout, which
                                   pulled it to its computed slot — the drag looked like it had been
                                   refused. A drop onto a hub is the exception: that node's position
                                   is now the new lane's business, and loadGraph clears the hold when
                                   it sees the membership change. */
                                d.fx = d.x; d.fy = d.y;
                                if (!moved) d.userPlaced = true;
                                // Brief low-alpha restart to let layout settle after edge change
                                if (!ev.active) {
                                    vm.simulation.alphaTarget(0.04).restart();
                                    setTimeout(() => vm.simulation.alphaTarget(0), 500);
                                }
                                return;
                            }

                            // Normal layout drag end — the node keeps where it was put.
                            dragTarget = null;
                            isDragging = false;
                            if (!ev.active) vm.simulation.alphaTarget(0);
                            if (d.type !== "cluster") {
                                d.fx = d.x; d.fy = d.y;
                                d.userPlaced = true;
                            } else if (!d.pinned) {
                                d.fx = null; d.fy = null;
                            }
                        })
                );

            /* Main filled circle */
            node.append("circle")
                .attr("class", "cgn-circle")
                .attr("r",            d => nr(d))
                .attr("fill",         d => this.nodeFill(d))
                .attr("stroke",       d => this.nodeCircleStroke(d).color)
                .attr("stroke-width", d => this.nodeCircleStroke(d).width)
                .style("filter",      `url(#${sid})`);

            /* ── "This one opens" ────────────────────────────────────────
               Which nodes have cross-cluster edges was only discoverable by reading a 8px "· 3
               groups" suffix, so there was no way to see where to click. A solid rose ring plus a
               count chip says it outright. Solid, deliberately: the dotted rose below means "a line
               you can see right now ends here", and the two must not be confused. */
            const openable = d => vm.canExpandNode(d) && !d.isRelationSubNode;
            /* The rose ring and the count chip say "this one has connections". Every group can be
               opened now, including groups with none — ringing those in rose claimed connections
               they do not have, and the chip beside them read "0", which is not a thing worth
               drawing. Openable is the cursor's business; connected is the ring's. */
            const connected = d => openable(d) && vm._linkedIdCount(d) > 0;

            node.filter(connected)
                .append("circle")
                .attr("class", "cgn-openable-ring")
                .attr("r",              d => nr(d) + 4)
                .attr("fill",           "none")
                .attr("stroke",         this.clusterEdgeWeight.color)
                .attr("stroke-width",   2)
                .attr("stroke-opacity", d => (vm.isNodeExpanded(d.id) ? 0.35 : 0.9))
                .attr("pointer-events", "none");

            /* Count chip: how many connection groups the node opens into, so its worth is legible
               before opening it. Sits top-right, outside the ring, clear of every label. */
            const connChip = node.filter(connected)
                .append("g")
                .attr("class", "cgn-conn-chip")
                .attr("transform", d => {
                    const o = (nr(d) + 4) * 0.72;
                    return `translate(${o},${-o})`;
                })
                .attr("pointer-events", "none");

            connChip.append("circle")
                .attr("r", 8.5)
                .attr("fill", this.clusterEdgeWeight.color)
                .attr("stroke", isDark ? "#0b1220" : "#ffffff")
                .attr("stroke-width", 1.8);

            connChip.append("text")
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("font-size", "9px")
                .attr("font-weight", "700")
                .attr("font-family", "ui-monospace,monospace")
                .attr("fill", "#ffffff")
                /* How many of its ids reach the other cluster — the number of exploded nodes
                   that will have lines. It used to be the bucket count, which no longer
                   corresponds to anything drawn. */
                .text(d => vm._linkedIdCount(d));

            /* Cluster-edge halo: this node sits at the end of a line that is currently drawn. Keyed
               to what is revealed, not to what the payload holds — ringing every node that has an
               edge somewhere marked almost all of them and told the curator nothing. */
            node.filter(d => !!d.showEdgeRing)
                .append("circle")
                .attr("class", "cgn-cluster-edge-ring")
                /* Outside the solid "opens" ring at +4, so a node can carry both. */
                .attr("r",               d => nr(d) + (connected(d) ? 9 : 5))
                .attr("fill",            "none")
                .attr("stroke",          this.clusterEdgeWeight.color)
                .attr("stroke-width",    this.clusterEdgeWeight.width)
                .attr("stroke-dasharray", CLUSTER_EDGE_DASH)
                .attr("stroke-linecap",  "round")
                .attr("pointer-events",  "none");

            /* Inner highlight ring */
            node.append("circle")
                .attr("r",    d => nr(d) * 0.68)
                .attr("fill", "rgba(255,255,255,0.10)")
                .attr("pointer-events", "none");

            /* Font Awesome icon */
            node.append("text")
                .attr("class", "cgn-icon")
                .attr("text-anchor",      "middle")
                .attr("dominant-baseline","central")
                .attr("dy",               "0.05em")
                .attr("font-family",      "'Font Awesome 6 Free','Font Awesome 5 Free','FontAwesome'")
                .attr("font-weight",      "900")
                .attr("font-size",        d => `${Math.round(nr(d) * (isLarge ? 0.48 : 0.52))}px`)
                .attr("fill",             "rgba(255,255,255,0.93)")
                .attr("pointer-events",   "none")
                .attr("style",            "text-rendering:geometricPrecision")
                .text(d => (d.isRelationSubNode ? "" : vm.getNodeIcon(d)));

            /* A slice shows how many edges its id has, in place of the glyph: at 62% scale a Font
               Awesome mark is unreadable. It used to show the id count, which is 1 for every one
               of them now that a slice is a single id — a column of "1"s saying nothing. */
            node.filter(d => !!d.isRelationSubNode)
                .append("text")
                .attr("class", "cgn-sub-count")
                .attr("text-anchor",       "middle")
                .attr("dominant-baseline", "central")
                .attr("font-size",         d => `${Math.round(nr(d) * 0.78)}px`)
                .attr("font-weight",       "700")
                .attr("font-family",       "ui-monospace,monospace")
                .attr("fill",              isDark ? "#e2e8f0" : "#0f172a")
                .attr("pointer-events",    "none")
                /* On the ladder the cable already shows a single join; only a fan-out needs a number. */
                .text(d => {
                    const n = Number(d.clusterEdgeCount) || 0;
                    return d.ladderSide ? (n > 1 ? n : "") : (n || "");
                });

            /* Expanded parents get a concentric ring, so which nodes are open is legible at a
               glance rather than something to infer from what is floating next to them. */
            node.filter(d => vm.isNodeExpanded(d.id))
                .append("circle")
                .attr("class", "cgn-expanded-ring")
                /* Outermost of the three, so it never sits under the other two. */
                .attr("r",              d => nr(d) + 14)
                .attr("fill",           "none")
                .attr("stroke",         d => vm.nodeFill(d))
                .attr("stroke-width",   1.6)
                .attr("stroke-opacity", 0.55)
                .attr("pointer-events", "none");

            /* Type label — up to two lines, so it stays narrow enough not to reach the node beside
               it (see _nodeLabelLines). */
            const labelY1 = d => nr(d) + (d.isRelationSubNode ? 13 : (isLarge ? 12 : 15));
            const labelY2 = d => labelY1(d) + (isLarge ? 10 : 11.5);
            const hasSecondLine = d => !!vm._nodeLabelLines(d)[1];
            const labelFont = d => (d.isRelationSubNode ? "10px" : (isLarge ? "9.5px" : "11px"));
            const styleLabel = sel => sel
                .attr("class",          "cgn-type")
                .attr("text-anchor",    "middle")
                .attr("letter-spacing", "0.01em")
                .attr("paint-order",    "stroke")
                .attr("stroke",         isDark ? "#0b1220" : "#f8fafc")
                .attr("stroke-width",   3)
                .attr("stroke-linejoin", "round")
                .attr("pointer-events", "none");

            /* Ladder slices label sideways: the id sits just above its line, on the side facing the
               other column, and the merge pill just below it. Stacked under the node like the rest,
               a label and pill per row needed a row three times the height, and the two columns'
               text collided in the gap. What the id reaches needs no text — the line shows it. */
            const ladder = d => !!d.ladderSide;
            const ladderOut = d => vm._ladderInward(d) * (nr(d) + 6);
            styleLabel(node.filter(ladder).append("text"))
                .attr("x",           ladderOut)
                .attr("y",           -7)
                .attr("text-anchor", d => (vm._ladderInward(d) > 0 ? "start" : "end"))
                .attr("font-size",   "10px")
                .attr("font-weight", "700")
                .attr("font-family", "ui-monospace,monospace")
                .attr("fill",        d => (d.relationUnconnected
                    ? (isDark ? "#64748b" : "#94a3b8")
                    : (isDark ? "#e2e8f0" : "#0f172a")))
                .text(d => { const l = vm._nodeLabelLines(d); return l[1] || l[0]; });

            /* Line 1 is the entity type — shared across a family, so it is deliberately quiet and
               small. It gives the category without competing with the name below it. */
            styleLabel(node.filter(d => !ladder(d)).append("text"))
                .attr("y",           labelY1)
                .attr("font-size",   d => (hasSecondLine(d) ? "9px" : labelFont(d)))
                .attr("font-weight", d => (hasSecondLine(d) ? "600" : "600"))
                .attr("letter-spacing", d => (hasSecondLine(d) ? "0.08em" : "0.01em"))
                .attr("fill",        d => (hasSecondLine(d)
                    ? (isDark ? "#64748b" : "#94a3b8")
                    : (isDark ? "#cbd5e1" : "#1e293b")))
                .text(d => vm._nodeLabelLines(d)[0]);

            /* Line 2 is what tells this node from its siblings, so it carries the weight. */
            styleLabel(node.filter(d => !ladder(d) && hasSecondLine(d)).append("text"))
                .attr("y",           labelY2)
                .attr("font-size",   labelFont)
                .attr("font-weight", "700")
                .attr("fill",        isDark ? "#e2e8f0" : "#0f172a")
                .text(d => vm._nodeLabelLines(d)[1]);

            /* Key / count label (below type) */
            node.filter(d => !ladder(d)).append("text")
                .attr("class",          "cgn-key")
                .attr("text-anchor",    "middle")
                /* Clears whichever label line is last. */
                .attr("y",              d => (hasSecondLine(d) ? labelY2(d) : labelY1(d)) + (isLarge ? 11 : 13))
                .attr("font-size",      d => (d.isRelationSubNode ? "8.5px" : (isLarge ? "8px" : "9.5px")))
                .attr("fill",           isDark ? "#64748b" : "#94a3b8")
                .attr("paint-order",    "stroke")
                .attr("stroke",         isDark ? "#0b1220" : "#f8fafc")
                .attr("stroke-width",   3)
                .attr("stroke-linejoin", "round")
                .attr("pointer-events", "none")
                .attr("font-family",    "ui-monospace,monospace")
                .text(d => vm.getKeyLabel(d, isLarge));

            /* ── Merge pill on each slice ──────────────────────────────────
               The action sits on the slice itself, not only in the side panel: the slice already
               states which connection it is, so "merge this one" needs no further context — and it
               keeps the decision on the object being decided about. */
            const pillLabel = d => (d.relationAllMerged ? "Merged" : `Merge ${(d.entityIds || []).length}`);
            const pillWidth = d => Math.max(52, pillLabel(d).length * 6.1 + 20);
            const pill = node.filter(d => !!d.isRelationSubNode && !d.relationUnconnected)
                .append("g")
                .attr("class", "cgn-merge-pill")
                .attr("transform", d => (ladder(d)
                    ? `translate(${ladderOut(d) + vm._ladderInward(d) * pillWidth(d) / 2},13)`
                    : `translate(0,${(hasSecondLine(d) ? labelY2(d) : labelY1(d)) + (d.isRelationSubNode ? 19 : 30)})`))
                .style("cursor", "pointer");

            pill.append("rect")
                .attr("x", d => -pillWidth(d) / 2)
                .attr("y", -9)
                .attr("width", d => pillWidth(d))
                .attr("height", 18)
                .attr("rx", 9)
                .attr("fill", d => (d.relationAllMerged
                    ? (isDark ? "#052e16" : "#d1fae5")
                    : (isDark ? "#4c0519" : "#fce7f3")))
                .attr("stroke", d => (d.relationAllMerged
                    ? (isDark ? "#34d399" : "#059669")
                    : vm.clusterEdgeWeight.color))
                .attr("stroke-width", 1);

            pill.append("text")
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("font-size", "9.5px")
                .attr("font-weight", "700")
                .attr("pointer-events", "none")
                .attr("fill", d => (d.relationAllMerged
                    ? (isDark ? "#34d399" : "#065f46")
                    : vm.clusterEdgeWeight.color))
                .text(d => (d.relationAllMerged ? "\u2713 Merged" : pillLabel(d)));

            pill.on("click", (ev, d) => {
                ev.stopPropagation();
                vm.$emit("merge-connection", {
                    nodeId: d.parentNodeId,
                    node: vm.graphNodes.find(n => n.id === d.parentNodeId) || d,
                    entityIds: (d.entityIds || []).map(String),
                    pairLabel: (d.relationTargets || []).join(" + "),
                });
            });

            /* ── Cursor: pointer where a click opens something, move where a drag works ── */
            node.style("cursor", d => {
                if (openable(d)) return "pointer";
                /* Slices are held by the fan force and merge via their pill, so dragging one to a
                   hub would be a second, conflicting way to move the same ids. */
                /* Exploded ids drag too now, so they say so. */
                if (vm.enableNodeMove && d.isRelationSubNode) return "move";
                if (vm.enableNodeMove && d.type !== "cluster") return "move";
                return null;
            });

            /* ── Interaction ── */
            node.on("click", (ev, d) => {
                ev.stopPropagation();
                vm._clearInspTimer();
                vm.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
                vm._highlightNeighbors(d, g);

                /* A group the payload divides fans out on click and shows its edge list. The old
                   id-selection panel stays reachable from a button in that panel, so the drag
                   workflow is not lost — it just is not what a click means for these nodes. */
                if (vm.canExpandNode(d)) {
                    vm.detailNode = d;
                    vm.toggleNodeExpanded(d.id);
                    return;
                }

                // In node-move mode, clicking an entity_group opens the ID selector panel.
                // Single-entity and cluster nodes open the detail panel as normal.
                if (vm.enableNodeMove && d.type === "entity_group" && !d.isRelationSubNode) {
                    const ids = d.entityIds && d.entityIds.length
                        ? d.entityIds
                        : (d.entitySubgroups || []).flatMap(sg => sg.entityIds || []);
                    if (ids.length > 1) {
                        vm.$emit("group-node-clicked", { nodeId: d.id, node: { ...d } });
                        vm.detailNode = null;  // close detail panel; parent shows selection panel
                        return;
                    }
                }

                vm.detailNode = d;
            });

            node.on("dblclick", (ev, d) => {
                ev.stopPropagation();
                d.pinned = !d.pinned;
                d.fx = d.pinned ? d.x : null;
                d.fy = d.pinned ? d.y : null;
                d3.select(ev.currentTarget).select("circle")
                    .attr("stroke",       () => this.nodeCircleStroke(d).color)
                    .attr("stroke-width", () => this.nodeCircleStroke(d).width);
            });

            node.on("mouseover", (ev, d) => {
                this._clearInspTimer();
                /* Thickening the "opens" ring under the cursor confirms the node is interactive
                   before the click, which the pointer cursor alone does not carry on a canvas
                   where most things are draggable anyway. */
                if (openable(d)) {
                    d3.select(ev.currentTarget).select(".cgn-openable-ring")
                        .transition().duration(120)
                        .attr("stroke-width", 3.5)
                        .attr("stroke-opacity", 1);
                }
                if (this.curationGraph) {
                    this._setInspectorFromNode(d);
                } else {
                    this._showFloatTip(ev, d, container);
                }
                if (d.ladderSide && this._ladderBridge) this._ladderBridge.hot(d);
            });

            node.on("mouseout", (ev, d) => {
                if (openable(d)) {
                    d3.select(ev.currentTarget).select(".cgn-openable-ring")
                        .transition().duration(160)
                        .attr("stroke-width", 2)
                        .attr("stroke-opacity", vm.isNodeExpanded(d.id) ? 0.35 : 0.9);
                }
                if (d.ladderSide && this._ladderBridge) this._ladderBridge.hot(null);
                if (this.curationGraph) {
                    this._scheduleInspHide();
                } else {
                    const tip = container.querySelector(".cgraph-float-tip");
                    if (tip) tip.style.opacity = "0";
                }
            });

            /* ── Focus ───────────────────────────────────────────────────
               With a node open, everything it does not concern recedes. Dimmed, not hidden: the
               cluster keeps its shape so the open node stays in context. */
            const focus = vm._focusIds;
            if (focus && focus.size) {
                node.attr("opacity", d => (focus.has(d.id) ? 1 : 0.1));
                link.attr("stroke-opacity", d => {
                    const sId = typeof d.source === "object" ? d.source.id : d.source;
                    const tId = typeof d.target === "object" ? d.target.id : d.target;
                    return (focus.has(sId) && focus.has(tId))
                        ? vm.linkOpacity(d)
                        : Math.min(0.07, vm.linkOpacity(d));
                });
            }

            /* ── Reveal ──────────────────────────────────────────────────
               The one animated moment on this canvas. Slices scale up from nothing in the order
               they fan, which is what makes "these came out of that node" readable; without it
               they simply appear and read as unrelated nodes that happened to arrive. */
            const newSubs = vm._newSubNodeIds || new Set();
            if (newSubs.size) {
                node.filter(d => newSubs.has(d.id))
                    .attr("opacity", 0)
                    .style("transform-box", "fill-box")
                    .transition()
                    .delay(d => 40 * (Number(d.relationIndex) || 0))
                    .duration(200)
                    .attr("opacity", 1);
                link.filter(d => d.linkKind === EXPAND_LINK_KIND)
                    .attr("stroke-opacity", 0)
                    .transition()
                    .delay(80)
                    .duration(220)
                    .attr("stroke-opacity", vm.linkOpacity({ linkKind: EXPAND_LINK_KIND }));
            }

            /* ── Tick ── */
            /* Lines run centre to centre, which would bury an arrowhead under the node it points
               at. Bidirectional edges are pulled back to each circle's rim instead — skipped when
               the two nodes are closer than their radii, where insetting would invert the line. */
            this.simulation.on("tick", () => {
                link.each(function (d) {
                    let x1 = d.source.x, y1 = d.source.y;
                    let x2 = d.target.x, y2 = d.target.y;
                    if (d.bidirectional && x1 != null && x2 != null) {
                        const dx = x2 - x1, dy = y2 - y1;
                        const len = Math.sqrt(dx * dx + dy * dy);
                        const rs = nr(d.source) + 2, rt = nr(d.target) + 2;
                        if (len > rs + rt + 8) {
                            const ux = dx / len, uy = dy / len;
                            x1 += ux * rs; y1 += uy * rs;
                            x2 -= ux * rt; y2 -= uy * rt;
                        }
                    }
                    this.setAttribute("x1", x1); this.setAttribute("y1", y1);
                    this.setAttribute("x2", x2); this.setAttribute("y2", y2);
                });
                if (vm._ladderBridge) vm._ladderBridge.update();
                /* One bad coordinate used to become `translate(1.5,NaN)` — invalid SVG, logged by
                   the browser on every tick for every node until the tab was closed. A node with
                   no usable position is parked at the centre instead, which is visible and wrong
                   rather than invisible and noisy. */
                node.attr("transform", d => (Number.isFinite(d.x) && Number.isFinite(d.y)
                    ? `translate(${d.x},${d.y})`
                    : `translate(${cx},${cyMid})`));
            });

            this.simulation.on("end", () => this.fitToScreen());
        },

        /* ── Inspector bar (curation hover) ──────────────────────────── */
        _clearInspTimer() {
            if (this.graphInspectorHideTimer != null) {
                clearTimeout(this.graphInspectorHideTimer);
                this.graphInspectorHideTimer = null;
            }
        },
        _scheduleInspHide() {
            this._clearInspTimer();
            this.graphInspectorHideTimer = setTimeout(() => {
                this.graphInspectorHideTimer = null;
                this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
            }, 450);
        },
        _setInspectorFromNode(d) {
            const conns = this._connCount(d);
            const color = this.nodeFill(d);
            const lines = [];
            let fullLabel = "";

            if (d.type === "entity_group") {
                /* Show full type~domain as the chip label; prefix if these are migrated IDs */
                fullLabel = (d.isMoved ? "→ " : "") + (d.neighborType || d.key || "Group");
                const ids = d.entityIds && d.entityIds.length
                    ? d.entityIds
                    : (d.entitySubgroups || []).flatMap(sg => sg.entityIds || []).map(String).filter(Boolean);
                lines.push(`${ids.length} ids`);
                /* The relation is why this slice is its own node, so it leads. */
                const rel = d.relationTargets || [];
                if (rel.length) lines.push(`joined to ${rel.join(", ")}`);
                else if (d.relationUnconnected) lines.push("no cross-cluster edge");
                if (d.clusterEdgeCount) lines.push(`${d.clusterEdgeCount} connection${d.clusterEdgeCount !== 1 ? "s" : ""}`);
                if (d.mergePreviewNew) lines.push("new from cluster 2");
                const groups = (d.relationGroups || []).length;
                if (groups) {
                    lines.push(this.isNodeExpanded(d.id)
                        ? "click to close"
                        : `click to show ${groups} connection group${groups !== 1 ? "s" : ""} and their edges`);
                } else if (!d.isRelationSubNode) {
                    lines.push("no cross-cluster edges");
                }
            } else if (d.type === "entity_bundle") {
                fullLabel = this.labels.entity_bundle || "Collapsed entities";
                lines.push(`${d.bundleTotal ?? "—"} total`);
                lines.push(`${d.bundleDisplayed ?? "—"} on graph`);
                lines.push(`${d.bundleOverflow ?? "—"} collapsed`);
                if (d.clusterEdgeCount) lines.push(`${d.clusterEdgeCount} cluster edge${d.clusterEdgeCount !== 1 ? "s" : ""}`);
                lines.push("click to expand");
            } else if (d.type === "entity") {
                fullLabel = String(d.key || "Entity");
                lines.push(`${conns} connection${conns !== 1 ? "s" : ""}`);
                if (d.clusterEdgeCount) lines.push(`${d.clusterEdgeCount} cluster edge${d.clusterEdgeCount !== 1 ? "s" : ""}`);
                if (d.inBothClusters) lines.push("in both clusters");
                if (d.mergePreviewNew) lines.push("new from cluster 2");
            } else if (d.type === "cluster") {
                const cnt = Number.isFinite(Number(d.hubConnectionCount)) ? Number(d.hubConnectionCount) : 0;
                if (d.isSplitNew) {
                    fullLabel = d.clusterFullId || d.key || `New Cluster ${Number(d.clusterIndex) - 1}`;
                    lines.push("drag nodes here to split");
                } else {
                    const idx = d.clusterIndex != null ? ` ${d.clusterIndex}` : "";
                    fullLabel = `Cluster${idx}${d.key ? ` · ${d.key}` : ""}`;
                }
                lines.push(`${cnt} node${cnt !== 1 ? "s" : ""} connected`);
            } else {
                fullLabel = this.labels[d.type] || d.type || "";
                if (d.key) lines.push(`key: ${d.key}`);
                lines.push(`${conns} connection${conns !== 1 ? "s" : ""}`);
                if (d.domain) lines.push(`domain: ${d.domain}`);
            }
            this.graphInspector = { visible: true, title: fullLabel, fullLabel, color, lines };
        },

        /* ── Float tooltip (non-curation) ────────────────────────────── */
        _showFloatTip(ev, d, container) {
            let tip = container.querySelector(".cgraph-float-tip");
            if (!tip) {
                tip = document.createElement("div");
                tip.className = "cgraph-float-tip";
                tip.style.cssText = "position:absolute;pointer-events:none;opacity:0;transition:opacity 0.15s;";
                container.appendChild(tip);
            }
            const conns = this._connCount(d);
            let extra = "";
            if (d.entityCount != null) extra += `<br>List size: ${d.entityCount}`;
            if (d.inBothClusters)      extra += `<br><em>In both clusters</em>`;
            if (d.neighborType)        extra += `<br>Type: <strong>${d.neighborType}</strong>`;
            if (d.neighborDomain)      extra += `<br>Domain: ${d.neighborDomain}`;
            tip.innerHTML = `<strong>${this.labels[d.type] || d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}<br>Connections: ${conns}${extra}`;
            tip.style.opacity = "1";
            const rect = container.getBoundingClientRect();
            tip.style.left = `${ev.clientX - rect.left + 16}px`;
            tip.style.top  = `${ev.clientY - rect.top  - 12}px`;
        },

        /* ── Highlight ────────────────────────────────────────────────── */
        _connCount(d) {
            return this.graphEdges.filter(e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                return s === d.id || t === d.id;
            }).length;
        },

        _highlightNeighbors(d, g) {
            const nb = new Set([d.id]);
            this.graphEdges.forEach(e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                if (s === d.id) nb.add(t);
                if (t === d.id) nb.add(s);
            });
            const dimN = (this.curationGraph && d.type === "cluster") ? 0.18 : 0.1;
            const dimL = dimN * 0.6;
            g.selectAll(".cgn").style("opacity", n => nb.has(n.id) ? 1 : dimN);
            g.selectAll(".cge")
                .style("opacity", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return s === d.id || t === d.id ? 0.95 : dimL;
                })
                .style("stroke", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return s === d.id || t === d.id ? "#f59e0b" : this.linkStroke(e);
                })
                .style("stroke-width", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return s === d.id || t === d.id ? 3 : this.linkWidth(e);
                });
        },

        _clearHighlight(g) {
            if (!g) { if (!this.svg) return; g = this.svg.select("g"); }
            g.selectAll(".cgn").style("opacity", 1);
            g.selectAll(".cge")
                .style("opacity", null)
                .style("stroke", e => this.linkStroke(e))
                .style("stroke-width", e => this.linkWidth(e));
        },

        /* ── Legend / filter ──────────────────────────────────────────── */
        _legendKey(d) {
            if (this.curationGraph && d.entityLegendKey) return d.entityLegendKey;
            return d.type;
        },

        toggleType(type) {
            if (type.startsWith("__")) return;
            if (this.hiddenTypes.has(type)) this.hiddenTypes.delete(type);
            else this.hiddenTypes.add(type);
            if (!this.svg) return;
            const g = this.svg.select("g");
            g.selectAll(".cgn").style("display", d => this.hiddenTypes.has(this._legendKey(d)) ? "none" : null);
            g.selectAll(".cge").style("display", e => {
                const s = typeof e.source === "object" ? e.source : { type: "" };
                const t = typeof e.target === "object" ? e.target : { type: "" };
                return this.hiddenTypes.has(this._legendKey(s)) || this.hiddenTypes.has(this._legendKey(t)) ? "none" : null;
            });
            /* The bridge is one drawing: hiding either column's type hides all of it. */
            g.selectAll(".cgl-bridge").style("display",
                this.graphNodes.some(n => n.ladderSide && this.hiddenTypes.has(this._legendKey(n))) ? "none" : null);
        },

        /* ── Zoom / view ──────────────────────────────────────────────── */
        zoomIn()  { if (this.svg && this.zoomBehavior) this.svg.transition().duration(280).call(this.zoomBehavior.scaleBy, 1.3); },
        zoomOut() { if (this.svg && this.zoomBehavior) this.svg.transition().duration(280).call(this.zoomBehavior.scaleBy, 0.77); },

        fitToScreen() {
            const t = this._fitTransform();
            if (!t) return;
            /* Held for the length of the animation so the panel can be placed against the view
               the graph is arriving at, not the one it is leaving. */
            this._pendingFit = t;
            clearTimeout(this._fitTimer);
            this._fitTimer = setTimeout(() => {
                this._pendingFit = null;
                this._placePanelIfAuto();
            }, 540);
            this.svg.transition().duration(500).call(
                this.zoomBehavior.transform,
                d3.zoomIdentity.translate(t.tx, t.ty).scale(t.scale)
            );
            this._placePanelIfAuto();
        },

        /**
         * The fit transform, without applying it — the panel placement needs it too.
         *
         * Returns null rather than a degenerate transform, and that matters more than it looks.
         * A zero-width container — mid-layout, hidden, or measured before CSS has settled — makes
         * `scale` 0, and d3's `constrain` inverts by the scale: `(x - tx) / 0` is NaN, so a zero
         * scale comes back as `translate(NaN,NaN)`. That poisons `__zoom` permanently, because
         * every later transform is derived from the one before it — the canvas goes blank and no
         * amount of zooming, dragging or re-fitting brings it back. It has to be refused here.
         */
        _fitTransform() {
            if (!this.svg || !this.zoomBehavior || !this.$refs.graphContainer) return null;
            const c = this.$refs.graphContainer;
            const W = c.clientWidth, H = c.clientHeight || 600;
            if (!(W > 0) || !(H > 0)) return null;
            /* The box is measured over each node's full extent, not its centre. A node carries a
               circle, two label lines and, on a slice, a merge pill — roughly 60px below and 30
               above — so fitting centres alone cropped the bottom row's labels every time. */
            let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
            this.graphNodes.forEach(n => {
                if (n.x == null || n.y == null) return;
                const r = this.nodeRadius(n);
                /* Three lines of text can sit under a node now: two label lines and the count. */
                const below = r + (n.isRelationSubNode ? 62 : 48);
                const side = Math.max(r, 66);
                /* A ladder's caption rides above its top row. */
                const above = n.ladderSide ? LADDER_ROW * 1.3 : r + 12;
                x0 = Math.min(x0, n.x - side); x1 = Math.max(x1, n.x + side);
                y0 = Math.min(y0, n.y - above); y1 = Math.max(y1, n.y + below);
            });
            /* All four bounds, not just x0. One node with a NaN y left x0 finite, so the guard
               passed and `ty` came out NaN — the same poisoned transform by another route. */
            if (![x0, x1, y0, y1].every(Number.isFinite)) return null;
            const pad = 28;
            const gW = (x1 - x0) || 1, gH = (y1 - y0) || 1;
            const scale = Math.min(W / (gW + 2 * pad), H / (gH + 2 * pad), 1);
            const tx = W / 2 - scale * (x0 + gW / 2);
            const ty = H / 2 - scale * (y0 + gH / 2);
            if (!(scale > 0) || !Number.isFinite(tx) || !Number.isFinite(ty)) return null;
            return { scale, tx, ty };
        },

        resetView() {
            this._clearInspTimer();
            this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
            this._clearHighlight();
            this.hiddenTypes = new Set();
            if (this.svg) {
                this.svg.select("g").selectAll(".cgn").style("display", null);
                this.svg.select("g").selectAll(".cge").style("display", null);
            }
            this.detailNode = null;
            /* Reset is the way back to the tidy layout: it releases every hand-placed and pinned
               node so they return to their slots. Without this, once a node had been dragged there
               was nothing that would ever put it back. */
            this.releaseNodePlacements();
            this.expandedNodeIds = [];
            this.primaryExpandedNodeId = null;
            this._redrawFromBase();
            this.fitToScreen();
        },

        /** Drops every manual position hold, in the live nodes and in the position cache. */
        releaseNodePlacements() {
            for (const n of this.graphNodes) {
                if (n.type === "cluster" || n.isRelationSubNode) continue;
                n.userPlaced = false;
                n.pinned = false;
                n.fx = null;
                n.fy = null;
            }
            if (this._posCache) {
                for (const entry of this._posCache.values()) {
                    entry.userPlaced = false;
                    entry.pinned = false;
                    entry.fx = null;
                    entry.fy = null;
                }
            }
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
            this.$nextTick(() => {
                if (this.svg) {
                    const c = this.$refs.graphContainer;
                    this.svg.attr("viewBox", `0 0 ${c.clientWidth} ${c.clientHeight || 600}`);
                }
                this.fitToScreen();
            });
        },

        /* ── Stats / legend population ────────────────────────────────── */
        _entityGroupIdCount(d) {
            if (!d || d.type !== "entity_group") return 0;
            const n = Number(d.idCountInNode);
            if (Number.isFinite(n) && n >= 0) return n;
            if (d.entityIds && d.entityIds.length) return d.entityIds.length;
            return (d.entitySubgroups || []).reduce((acc, s) => acc + ((s.entityIds && s.entityIds.length) || 0), 0);
        },

        updateStats() {
            const shared  = this.graphNodes.filter(n => n.inBothClusters).length;
            const collapsed = this.graphNodes.filter(n => n.type === "entity_bundle")
                .reduce((sum, n) => sum + (Number(n.bundleOverflow) || 0), 0);
            const drawnClusterEdges = this.graphEdges.filter(
                e => e.linkKind === CLUSTER_EDGE_LINK_KIND
            ).length;
            this.graphStats = {
                nodes: this.graphNodes.length,
                edges: this.graphEdges.length,
                sharedEntities: shared,
                collapsedEntities: collapsed,
                drawnClusterEdges,
            };

            const tc = {};
            /* Slices are drawn but not counted: they carry their parent's legend key, so counting
               them would claim more of a type~domain than the cluster actually holds. */
            this.graphNodes.forEach(n => {
                if (n.isRelationSubNode) return;
                const k = this._legendKey(n);
                tc[k] = (tc[k] || 0) + 1;
            });
            const sorted = Object.entries(tc).sort((a, b) => b[1] - a[1]);
            this.legendItems = sorted.map(([type, count]) => {
                if (type.startsWith("entity:")) {
                    const nt = type.slice(7);
                    if (nt.length) {
                        /* Use full "type~domain" key so legend chip color matches the node color */
                        const fill = nt === "user_composite"
                            ? (this.colors["entity:user_composite"] || this.getColor("entity_group"))
                            : (this.prefixColorMap.get(nt.toLowerCase())
                                || this.getColor("entity_group"));
                        const label = nt === "user_composite"
                            ? "Custom composite"
                            : (this.curationGraph ? this.shortPairLabel(nt, 30) : `${nt} (entity)`);
                        /* The chip is shortened the same way node labels are, so the full key has
                           to stay reachable somewhere — it goes on the chip's tooltip. */
                        return { type, label, full: nt, color: fill, count, legendOverflow: false };
                    }
                }
                const dynColor = this.prefixColorMap.get(type) || this.getColor(type) || "#64748b";
                return { type, label: this.labels[type] || type, color: dynColor, count, legendOverflow: false };
            });
            this.legendExpanded = false;
        },

        /* ── Public util ──────────────────────────────────────────────── */
        downloadTextFile(filename, text) {
            const blob = new Blob([text == null ? "" : String(text)], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = Object.assign(document.createElement("a"), { href: url, download: filename, rel: "noopener" });
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },
    },
};
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────────────────── */
.cgraph-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    border-radius: inherit;
}
.cgraph-fullscreen {
    position: fixed !important;
    inset: 0;
    z-index: 1050;
    border-radius: 0 !important;
}
.cgraph-fullscreen .cgraph-canvas { flex: 1; min-height: unset; }

/* ── Toolbar ───────────────────────────────────────────────────────── */
.cgraph-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 12px;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    gap: 8px;
    flex-wrap: wrap;
}
.cgraph-tb-left  { display: flex; align-items: center; gap: 8px; }
.cgraph-tb-right { display: flex; align-items: center; gap: 4px; }
.cgraph-tb-sep   { width: 1px; height: 18px; background: #e2e8f0; margin: 0 4px; flex-shrink: 0; }

.cgraph-tb-btn {
    width: 26px; height: 26px;
    border: 1px solid #e2e8f0;
    border-radius: 7px;
    background: transparent;
    color: #64748b;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.cgraph-tb-btn:hover { background: #f1f5f9; color: #1e293b; border-color: #cbd5e1; }

.cgraph-stats { font-size: 11.5px; font-weight: 500; color: #475569; display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.cgraph-stats-sep     { color: #cbd5e1; }
.cgraph-stats-shared  { color: #3b82f6; font-weight: 600; }
.cgraph-stats-cluster-edges { color: #be185d; font-weight: 600; }
.cgraph-stats-cluster-edges.is-idle { color: #94a3b8; font-weight: 500; }
/* ── Canvas area wrapper (inspector floats here) ───────────────────── */
.cgraph-canvas-area {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.cgraph-lane-hdrs {
    position: absolute;
    top: 8px;
    left: 0;
    right: 0;
    z-index: 3;
    display: flex;
    justify-content: space-between;
    padding: 0 12px;
    pointer-events: none;
}
.cgraph-lane-hdr {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    max-width: 46%;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid var(--lane-color);
    background: #fff;
    background: color-mix(in srgb, var(--lane-color) 12%, #fff);
    font-size: 11px;
    font-weight: 700;
    color: var(--lane-color);
    pointer-events: auto;
}
.cgraph-lane-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--lane-color);
    flex-shrink: 0;
    align-self: center;
}
.cgraph-lane-name { white-space: nowrap; }
.cgraph-lane-id {
    font-family: monospace;
    font-size: 10px;
    font-weight: 500;
    opacity: 0.75;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
body.dark-mode .cgraph-lane-hdr {
    background: #1e293b;
    background: color-mix(in srgb, var(--lane-color) 22%, #0f172a);
    color: #e2e8f0;
}

/* ── Inspector bar — absolutely positioned, never shifts layout ─────── */
.cgraph-inspector {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(6px);
    border-bottom: 1px solid #bfdbfe;
    border-left: 3px solid #3b82f6;
    font-size: 12px;
    line-height: 1.5;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.07);
    pointer-events: none;
}
/* Chip: colored dot + full type~domain label */
.cgraph-insp-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: color-mix(in srgb, var(--ic, #3b82f6) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--ic, #3b82f6) 35%, transparent);
    border-radius: 20px;
    padding: 2px 10px 2px 5px;
    font-weight: 600;
    font-size: 12px;
    max-width: 320px;
    overflow: hidden;
    white-space: nowrap;
}
.cgraph-insp-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--ic, #3b82f6);
    flex-shrink: 0;
}
.cgraph-insp-label {
    overflow: hidden;
    text-overflow: ellipsis;
    color: color-mix(in srgb, var(--ic, #1e3a5f) 80%, #000);
}
.cgraph-insp-meta  { color: #475569; font-size: 12px; }
.cgraph-inspector-sep { margin: 0 4px; opacity: 0.5; }

/* Slide-down transition */
.cgraph-insp-enter-active, .cgraph-insp-leave-active { transition: transform 0.15s ease, opacity 0.15s ease; }
.cgraph-insp-enter-from, .cgraph-insp-leave-to { transform: translateY(-100%); opacity: 0; }

/* ── Legend ────────────────────────────────────────────────────────── */
.cgraph-legend {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: thin;
    gap: 4px;
    padding: 5px 12px;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    /* The row scrolls, so the last chip was being sliced clean in half at the edge and read as a
       rendering fault. Fading the final 28px says "there is more" instead. */
    mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent 100%);
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent 100%);
}
.cgraph-legend.is-wrapped {
    mask-image: none;
    -webkit-mask-image: none;
}
/* Scanning mode: wrap, but never past a third of a short viewport. */
.cgraph-legend.is-wrapped {
    flex-wrap: wrap;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 5.5rem;
}
.cgraph-legend::-webkit-scrollbar { height: 6px; }
.cgraph-legend::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
.cgraph-legend-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 9px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: #fff;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    font-size: 11.5px;
    color: #334155;
    transition: all 0.14s;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.cgraph-legend-chip:hover { border-color: #94a3b8; box-shadow: 0 2px 6px rgba(0,0,0,0.09); transform: translateY(-1px); }
.cgraph-legend-chip.is-off { opacity: 0.38; background: #f8fafc; box-shadow: none; transform: none; }
/* Pinned to the right edge so the toggle stays reachable while the row scrolls. */
.cgraph-legend-more {
    border-style: dashed;
    color: #64748b;
    font-size: 11px;
    position: sticky;
    right: 0;
    background: #fff;
    box-shadow: -6px 0 6px -4px rgba(15, 23, 42, 0.12);
}
.cgraph-legend.is-wrapped .cgraph-legend-more { position: static; box-shadow: none; }
.cgraph-legend-dot  { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; box-shadow: 0 1px 3px rgba(0,0,0,0.22); }
.cgraph-legend-label { font-weight: 500; }
.cgraph-legend-cnt   { color: #94a3b8; font-size: 11px; }

/* ── Canvas ────────────────────────────────────────────────────────── */
.cgraph-canvas {
    position: relative;
    flex: 1;
    width: 100%;
    min-height: 0;
    cursor: grab;
    background-color: #f8fafc;
    background-image: radial-gradient(circle, #d1d5db 1px, transparent 1px);
    background-size: 28px 28px;
}
.cgraph-canvas:active { cursor: grabbing; }

/* ── Detail panel ──────────────────────────────────────────────────────
   An overlay, not a dock. Docked below the canvas it was a flex item and took its height out of
   `.cgraph-canvas-area` (`flex: 1`), shrinking the graph — so it is absolute again, sized to leave
   a corner free, and seated by `_bestPanelSlot` into whichever corner is emptiest. */
.cgraph-panel {
    position: absolute;
    top: 108px;
    right: 14px;
    width: 288px;
    /* Short enough that a corner is a real slot and the opposite corner stays usable. */
    max-height: min(52%, calc(100% - 122px));
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 20px 56px rgba(15, 23, 42, 0.17), 0 4px 14px rgba(0, 0, 0, 0.07);
    z-index: 30;
    overflow: hidden;
    /* Eases from one slot to the next, so an automatic move reads as the panel getting out of
       the way rather than as the panel jumping. Dragging turns it off — see `--placed`. */
    transition: left 0.26s cubic-bezier(0.4, 0, 0.2, 1), top 0.26s cubic-bezier(0.4, 0, 0.2, 1);
}
.cgraph-panel--wide { width: min(340px, 92vw); }
.cgraph-panel--collapsed { max-height: none; }
/* Positioned by script: no transition, or it lags the pointer through the whole drag. */
.cgraph-panel--placed { transition: none; }

.cgraph-panel-hdr {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    flex-shrink: 0;
    background: linear-gradient(135deg, #152844 0%, #1d3461 100%);
    border-left: 4px solid var(--node-color, #3b82f6);
    user-select: none;
}
.cgraph-panel-hdr { cursor: grab; }
.cgraph-panel-hdr:active { cursor: grabbing; }
.cgraph-panel-hdr-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
.cgraph-panel-btn {
    width: 26px; height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 7px;
    background: rgba(255,255,255,0.14);
    color: #fff;
    font-size: 11px;
    cursor: pointer;
}
.cgraph-panel-btn:hover { background: rgba(255,255,255,0.3); }
.cgraph-panel-hdr-inner  { display: flex; align-items: center; gap: 10px; min-width: 0; }
.cgraph-panel-icon-badge {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: rgba(255,255,255,0.18);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #fff;
    flex-shrink: 0;
}
.cgraph-panel-hdr-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cgraph-panel-btn { transition: background 0.12s; }

.cgraph-panel-body {
    padding: 14px 16px;
    font-size: 12.5px;
    line-height: 1.85;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
}

/* Panel content atoms */
.cgraph-panel-meta-row  { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.cgraph-type-pill {
    display: inline-block;
    padding: 2px 10px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
    color: #1e40af;
    font-family: ui-monospace, monospace;
    word-break: break-all;
}
.cgraph-cnt-badge { background: #f1f5f9; border-radius: 20px; padding: 2px 9px; font-size: 11.5px; font-weight: 600; color: #64748b; }
/* Read-only id table: id, how many edges it has, and which type~domains they reach. */
.cgraph-idinfo {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    overflow: hidden;
    margin-bottom: 6px;
}
.cgraph-idinfo-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1.4fr);
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    border-bottom: 1px solid #f1f5f9;
    border-left: 3px solid transparent;
}
.cgraph-idinfo-row:last-child { border-bottom: none; }
/* An id with edges is the reason the panel is open, so it is the one that stands out. */
.cgraph-idinfo-row.is-linked { border-left-color: #f43f5e; background: #fff; }
.cgraph-idinfo-id {
    font-family: ui-monospace, monospace;
    font-size: 11.5px;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cgraph-idinfo-cnt {
    font-size: 10.5px;
    font-weight: 700;
    color: #be185d;
    white-space: nowrap;
}
.cgraph-idinfo-cnt.is-zero { color: #94a3b8; font-weight: 500; }
.cgraph-idinfo-targets { display: flex; flex-wrap: wrap; gap: 4px; min-width: 0; }
.cgraph-idinfo-sub {
    margin-left: 4px;
    padding-left: 4px;
    border-left: 1px solid rgba(30, 64, 175, 0.25);
    opacity: 0.75;
}
.cgraph-id-list { max-height: 260px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; margin-bottom: 6px; }
.cgraph-id-row  { padding: 5px 12px; font-family: ui-monospace, monospace; font-size: 11.5px; color: #334155; border-bottom: 1px solid #f1f5f9; word-break: break-all; }
.cgraph-id-row:last-child { border-bottom: none; }
.cgraph-edge-row   { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.cgraph-edge-from  { font-weight: 700; color: #be185d; }
.cgraph-edge-arrow { font-size: 9px; color: #be185d; opacity: 0.8; }
.cgraph-edge-pair  { font-family: inherit; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; }
.cgraph-edge-to    { flex: 1 1 auto; min-width: 0; }

/* Expandable id rows: the id, its connection count, and the edges it opens into. */
.cgraph-type-pill--sm { padding: 1px 7px; font-size: 10.5px; margin-right: 4px; }

/* Connection groups — the panel twin of the fanned-out sub-nodes. */
.cgraph-rel-none    { font-size: 11px; font-style: italic; color: #94a3b8; }

/* Merge pill drawn on a sub-node. */
:deep(.cgn-merge-pill rect) { transition: fill 0.15s ease; }
:deep(.cgn-merge-pill:hover rect) { filter: brightness(0.95); }

.cgraph-empty-note { color: #94a3b8; font-size: 12px; margin: 0; }
.cgraph-panel-note  { color: #64748b; font-size: 12px; margin: 0 0 10px; }

.cgraph-stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 7px;
    margin-bottom: 12px;
}
.cgraph-stat-cell {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.cgraph-stat-lbl       { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; line-height: 1.2; }
.cgraph-stat-val       { font-size: 1.2rem; font-weight: 700; color: #1e293b; line-height: 1.15; font-variant-numeric: tabular-nums; }
.cgraph-stat-val--warn { color: #d97706; }
.cgraph-btn-row        { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.cgraph-section-lbl    { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; margin: 8px 0 5px; }
.cgraph-mono-val       { font-family: ui-monospace, monospace; font-size: 12px; color: #1e293b; word-break: break-all; background: #f1f5f9; border-radius: 6px; padding: 6px 10px; margin: 4px 0; }
.cgraph-flag-row       { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.cgraph-flag           { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; }
.cgraph-flag--amber    { background: #fef3c7; color: #92400e; }
.cgraph-flag--rose     { background: #fce7f3; color: #9d174d; }
.cgraph-flag--muted    { background: #f1f5f9; color: #64748b; }
.cgraph-flag--green    { background: #d1fae5; color: #065f46; }
.cgraph-kv             { display: flex; flex-direction: column; margin-bottom: 7px; }
.cgraph-kv-lbl         { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin-bottom: 1px; }
.cgraph-kv-val         { font-size: 13px; color: #1e293b; }

/* Float tooltip */
:deep(.cgraph-float-tip) {
    position: absolute;
    background: rgba(15, 23, 42, 0.95);
    color: #e2e8f0;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12.5px;
    line-height: 1.65;
    box-shadow: 0 8px 24px rgba(0,0,0,0.28);
    max-width: 300px;
    z-index: 20;
    border: 1px solid rgba(255,255,255,0.08);
    pointer-events: none;
}

/* ── Panel slide transition ────────────────────────────────────────── */
.cgraph-slide-enter-active { transition: transform 0.24s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease; }
.cgraph-slide-leave-active { transition: transform 0.18s ease, opacity 0.15s ease; }
.cgraph-slide-enter-from,
.cgraph-slide-leave-to     { transform: translateX(18px) scale(0.97); opacity: 0; }
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

/* ── Ladder bridge (D3-created, so unscoped) ─────────────────────────── */
.cgl-bridge {
    .cgl-rung,
    .cgl-band {
        transition: opacity 0.18s ease;
    }
    .cgl-cable {
        transition: stroke-width 0.18s ease, stroke-opacity 0.18s ease;
    }
    .cgl-mark {
        transition: r 0.18s ease;
    }
    &.has-hot {
        .cgl-rung:not(.is-hot) { opacity: 0.14; }
        .cgl-band:not(.is-hot) { opacity: 0.35; }
        .cgl-rung.is-hot .cgl-cable { stroke-width: 2.8px; stroke-opacity: 1; }
        .cgl-rung.is-hot .cgl-mark  { r: 4.6px; }
        .cgl-band.is-hot { fill-opacity: 0.14; }
    }
}
.cgn.cgn-ladder-cold { opacity: 0.32; transition: opacity 0.18s ease; }
/* The circle's shadow filter is an inline style, so the emphasis is carried by the stroke. */
.cgn.cgn-ladder-hot > .cgn-circle {
    stroke: #e11d48;
    stroke-width: 3px;
}
body.dark-mode .cgn.cgn-ladder-hot > .cgn-circle { stroke: #fb7185; }
@media (prefers-reduced-motion: reduce) {
    .cgl-bridge .cgl-rung,
    .cgl-bridge .cgl-band,
    .cgl-bridge .cgl-cable,
    .cgl-bridge .cgl-mark,
    .cgn.cgn-ladder-cold { transition: none; }
}

body.dark-mode {
    .cgraph-toolbar {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }
    .cgraph-tb-btn {
        color: $dark-text-muted;
        border-color: $dark-border-color;
    }
    .cgraph-tb-btn:hover {
        background: $dark-border-color;
        color: $dark-text-primary;
    }
    .cgraph-tb-sep { background: $dark-border-color; }
    .cgraph-stats  { color: $dark-text-muted; }
    .cgraph-stats-sep { color: $dark-border-color; }
    .cgraph-stats-cluster-edges { color: #fb7185; }
    .cgraph-inspector {
        background: rgba(15, 23, 42, 0.97);
        border-bottom-color: #1e3a5f;
        border-left-color: #3b82f6;
    }
    .cgraph-insp-chip { color: #e2e8f0; }
    .cgraph-insp-meta { color: $dark-text-muted; }

    .cgraph-legend {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }
    .cgraph-legend-chip {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;
    }
    .cgraph-legend-chip:hover { border-color: #4a5568; }
    .cgraph-legend-more { background: $dark-elevated-bg; box-shadow: -6px 0 6px -4px rgba(0,0,0,0.5); }
    .cgraph-legend::-webkit-scrollbar-thumb { background: #4a5568; }
    .cgraph-legend-cnt { color: $dark-text-muted; }

    .cgraph-canvas {
        background-color: $dark-card-bg;
        background-image: radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
    }

    .cgraph-panel {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        box-shadow: 0 20px 56px rgba(0,0,0,0.65), 0 4px 14px rgba(0,0,0,0.45);
    }
    .cgraph-panel-hdr { background: linear-gradient(135deg, #0d1e35 0%, #132040 100%); }
    .cgraph-panel-body { color: $dark-text-primary; }

    .cgraph-type-pill { background: #1e3a5f; border-color: #2d4a6d; color: #93c5fd; }
    .cgraph-cnt-badge { background: $dark-border-color; color: $dark-text-muted; }
    .cgraph-id-list   { background: $dark-body-bg; border-color: $dark-border-color; }
    .cgraph-id-row    { color: $dark-text-primary; border-bottom-color: $dark-border-color; }
    .cgraph-edge-from, .cgraph-edge-arrow { color: #fb7185; }
    .cgraph-edge-pair { color: $dark-text-muted; }


    .cgraph-rel-none     { color: $dark-text-muted; }

    .cgraph-stat-cell { background: $dark-body-bg; border-color: $dark-border-color; }
    .cgraph-stat-lbl  { color: $dark-text-muted; }
    .cgraph-stat-val  { color: $dark-text-primary; }

    .cgraph-mono-val  { background: $dark-body-bg; color: $dark-text-primary; }
    .cgraph-kv-val    { color: $dark-text-primary; }
    .cgraph-section-lbl { color: $dark-text-muted; }

    .cgraph-flag--amber { background: #451a03; color: #fbbf24; }
    .cgraph-flag--green { background: #052e16; color: #34d399; }
    .cgraph-flag--rose  { background: #4c0519; color: #fda4af; }
    .cgraph-flag--muted { background: $dark-border-color; color: $dark-text-muted; }

    .cgraph-panel-btn:hover { background: rgba(255,255,255,0.25); }
}
</style>
